import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, arrayUnion, setDoc, increment, arrayRemove, deleteDoc, deleteField, orderBy, limit, } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faCalendarCheck, 
  faRoute, 
  faEnvelope,
  faHome,
  faUser,
  faCog,
  faSignOutAlt,
  faWallet,
  faShare,
  faEdit,
  faVideo,
  faMusic,
  faImage,
  faTimes,
  faPlus,
  faLink,
  faTrash,
  faSpinner,
  faThumbsUp,
  faComment,
  faEllipsisV,
  faCheck,
  faClock,
  faFlag,
  faUpload,
  faUserPlus,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faInfoCircle,
  faLock,
  faImages,
  faPlay,
  faScissors,
  faArrowUp,
  faFilePdf,
  faCalendarAlt,
  faExternalLinkAlt,
  faUserShield,
  faFileLines,
  faBuilding,
  faPenToSquare,
  faStar,
  faStarHalf,
  faStore,
  faBriefcase 
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTwitter, 
  faFacebook, 
  faYoutube,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';
import spotifylogo from '../../assets/Images/spotifyicon.png';
import applemusiclogo from '../../assets/Images/applemusicicon.png';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './Profile.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import defaultCoverPhoto from '../../assets/Images/defaultUserCoverPicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import { createPostWithMedia, deleteMediaPost, deletePost, updatePost, getUserPosts, validatePostUrl, getUserTags } from '../../utils/actions/PostActions';
import ShareModal from '../common/ShareModal';
import BrandWebsiteModal from '../common/BrandWebsiteModal';
import SearchComponent from '../common/SearchComponent';
import { ToursModal, ShowsModal, UsersModal } from './ProfileModals';
import BrandsModal from '../common/BrandsModal';
import { getAuth } from "firebase/auth";
import { NOTIFICATION_TYPES, sendRecommendationRequestNotification, sendEventInvitationNotification, createNotification } from '../../utils/NotificationService';
import { checkRecommendationCooldown, sendRecommendationRequest, formatCooldownTimeRemaining } from '../../utils/RecommendationService';
import VendorInventoryTab from './VendorInventoryTab';
import PostModal from '../common/PostModal';
import FileViewerModal from '../common/FileViewerModal'
import { usePostContext } from '../../context/PostContext';
import AudioTrimmerModal from '../common/AudioTrimmerModal';
import VideoTrimmerModal from '../common/VideoTrimmerModal';

// Import modals from separate components
import ReportModal from '../profileModals/ReportModal';
import UploadModal from '../profileModals/UploadModal';
import ConnectionsModal from '../profileModals/ConnectionsModal';
import RecommendationsModal from '../profileModals/RecommendationsModal';
import TourInvitationModal from '../profileModals/TourInvitationModal';
import UpgradeModal from '../profileModals/UpgradeModal';
import CustomModal from '../profileModals/CustomModal';
import AlertModal from '../profileModals/AlertModal';
import { 
  DisconnectConfirmModal, 
  UnrecommendConfirmModal, 
  CancelRequestModal 
} from '../profileModals/ConfirmationModals';
import { 
  MessageRestrictionModal, 
  EmailPermissionModal 
} from '../profileModals/MessageModals';
import EPKSection from '../EPKSection/EPKSection';
import EPKBuilder from '../EPKSection/EPKBuilder';
import EPKViewer from '../EPKSection/EPKViewer';

import { 
  StarRating, 
  OrganizationsSection, 
  StaffSection, 
  AssociatedVendorsSection, 
  ManagingActsSection, 
  ManagersSection, 
  ManagedBySection,
  ExecutivesSection, 
  ContactsSection 
} from './ProfileComponents';

import {
  getCachedExperienceYears,
  cacheExperienceYears,
  getCurrentYear,
  calculateExperienceYears,
  shouldUpdateExperienceYears,
  updateExperienceYearsInFirestore
} from '../../utils/experienceUtils';

import RatingsReviewsSection from './RatingsReviewsSection';
import FeedbackSection from './FeedbackSection';


// StatsContainer Component
const StatsContainer = ({ profileData, events, currentUser, onToursClick, onShowsClick, onRecommendationsClick, onConnectionsClick }) => {
  // Check if an event has any verified dates across all personnel types
  const hasEventVerifiedDates = (event) => {
    // For a specific user profile, only count their verified dates
    if (profileData.userId !== currentUser.userId) {
      // In the new structure, we need to check the assignedUsers subcollection
      // We assume the data has been fetched and added to the event object already
      if (!event.assignedUsers) return false;
      
      // Find the user in the assignedUsers array
      const userAssignment = event.assignedUsers.find(user => 
        user.userId === profileData.userId
      );
      
      // Check if any assigned dates are verified
      return userAssignment?.assignedDates?.some(date => date.verified === true) || false;
    } else {
      // For your own profile, check across all assigned users
      if (!event.assignedUsers || event.assignedUsers.length === 0) return false;
      
      // Check if any user has any verified dates
      return event.assignedUsers.some(user => 
        user.assignedDates?.some(date => date.verified === true)
      );
    }
  };

  // Count tours that have at least one verified date across all personnel types
  const countToursWithVerifiedDates = () => {
    return events.filter(event => event.type === 'tour' && hasEventVerifiedDates(event)).length;
  };

  // Count all verified shows across all events for this specific user
  const countVerifiedShows = () => {
    let totalVerifiedShows = 0;
    
    events.forEach(event => {
      // Use a set to avoid counting duplicates
      const verifiedDateIds = new Set();
      
      if (profileData.userId !== currentUser.userId) {
        // For other users' profiles, only count their verified dates
        // Find the user in the assignedUsers array
        const userAssignment = event.assignedUsers?.find(user => 
          user.userId === profileData.userId
        );
        
        // Count verified dates for the specific user
        if (userAssignment?.assignedDates) {
          userAssignment.assignedDates.forEach(date => {
            if (date.verified === true) {
              verifiedDateIds.add(date.dateDocId);
            }
          });
        }
      } else {
        // For own profile, count all verified dates across all assigned users
        if (event.assignedUsers) {
          event.assignedUsers.forEach(user => {
            if (user.assignedDates) {
              user.assignedDates.forEach(date => {
                if (date.verified === true) {
                  verifiedDateIds.add(date.dateDocId);
                }
              });
            }
          });
        }
      }
      
      totalVerifiedShows += verifiedDateIds.size;
    });
    
    return totalVerifiedShows;
  };

  return (
    <div className={styles['stats-grid']}>
      <div className={styles['stat-card']} onClick={onToursClick}>
        <div className={styles['stat-value']}>{countToursWithVerifiedDates()}</div>
        <div className={styles['stat-label']}>Tours</div>
      </div>
      <div className={styles['stat-card']} onClick={onShowsClick}>
        <div className={styles['stat-value']}>
          {countVerifiedShows()}
        </div>
        <div className={styles['stat-label']}>Shows</div>
      </div>
      <div className={styles['stat-card']} onClick={onConnectionsClick}>
        <div className={styles['stat-value']}>{profileData?.connectionsCount || 0}</div>
        <div className={styles['stat-label']}>Connections</div>
      </div>
      <div className={styles['stat-card']} onClick={onRecommendationsClick}>
        <div className={styles['stat-value']}>{profileData?.recommendationsCount || 0}</div>
        <div className={styles['stat-label']}>
          <span className={styles['rec-text-desktop']}>Recommends</span>
          <span className={styles['rec-text-mobile']}>Recommends</span>
        </div>
      </div>
    </div>
  );
};

// EventDates Component (formerly TourDates)
const EventDates = ({ userData, events }) => {
  const [filter, setFilter] = useState('all');
  const [displayDays, setDisplayDays] = useState(30);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Date formatting function
  const formatToDateString = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Generate dates function
  const generateDates = () => {
    const datesList = [];
    const today = new Date();
    // Reset time to start of day in local time
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < displayDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      datesList.push(date);
    }
    return datesList;
  };

  const checkBookingStatus = (date) => {
    // Convert input date to YYYY-MM-DD format in local time
    const dateString = formatToDateString(date);

    for (const event of events) {
      const eventType = event.type || 'tour';
      
      // Find user in assignedUsers based on userData
      if (!event.assignedUsers) continue;
      
      const userAssignment = event.assignedUsers.find(user => 
        user.userId === userData.userId
      );
      
      if (!userAssignment || !userAssignment.assignedDates) continue;
      
      for (const assignedDate of userAssignment.assignedDates) {
        // Use eventDates for consistency with new structure
        const eventDate = event.eventDates?.find(td => 
          td.id === assignedDate.dateDocId
        );
        
        if (!eventDate || eventDate.cancelled) continue;
        
        const originalDate = formatToDateString(eventDate.date);
        const hasReschedule = eventDate.rescheduleDate?.isRescheduled;
        const rescheduledDate = hasReschedule ? 
          formatToDateString(eventDate.rescheduleDate.newDate) : null;
          
        if ((originalDate === dateString && !hasReschedule) || 
            (hasReschedule && rescheduledDate === dateString)) {
          return {
            isBooked: true,
            eventName: eventType === 'tour' ? event.tourName : event.showName,
            actName: event.actName,
            venue: eventDate.event?.venueDetails?.venueData,
            rescheduleInfo: hasReschedule && rescheduledDate === dateString ? {
              isRescheduled: true,
              originalDate: originalDate
            } : null
          };
        }
      }
    }

    return null;
  };

  // Filter dates based on selected filter
  const getFilteredDates = () => {
    if (filter === 'byDate') {
      return [selectedDate];
    }

    const allDates = generateDates();
    
    switch (filter) {
      case 'booked':
        return allDates.filter(date => checkBookingStatus(date));
      case 'available':
        return allDates.filter(date => !checkBookingStatus(date));
      default:
        return allDates;
    }
  };

  // Handle calendar click
  const handleCalendarClick = () => {
    // This will programmatically click the date input
    document.getElementById('date-picker-input').showPicker();
  };

  // Format date for display
  const formatDisplayDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return 'Today, ' + date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } else if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow, ' + date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } else {
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  const renderDateCard = (date) => {
    const bookingStatus = checkBookingStatus(date);
    const isBooked = !!bookingStatus;
  
    return (
      <div key={date.toISOString()} className={styles['date-card']}>
        <div className={styles['date-header']}>
          <div className={styles.date}>
            {formatDisplayDate(date)}
          </div>
          <div className={`${styles.status} ${isBooked ? styles['on-tour'] : styles.available}`}>
            {isBooked ? 'Booked' : 'Available'}
          </div>
        </div>
  
        {isBooked && bookingStatus.venue && (
          <div className={styles['tour-info']}>
            <h4>{bookingStatus.eventName}</h4>
            <p className={styles['act-name']}>{bookingStatus.actName}</p>
            <div className={styles['venue-info']}>
              <p className={styles['venue-name']}>
                {bookingStatus.venue.displayName}
              </p>
              <p className={styles['venue-location']}>
                {bookingStatus.venue.formattedAddress}
              </p>
            </div>
            {bookingStatus.rescheduleInfo && (
              <p className={styles['rescheduled-info']}>
                Rescheduled from: {new Date(bookingStatus.rescheduleInfo.originalDate).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles['dates-container']}>
      <div className={styles['date-filters']}>
        <button 
          className={`${styles['filter-btn']} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          All Dates
        </button>
        <button 
          className={`${styles['filter-btn']} ${filter === 'booked' ? styles.active : ''}`}
          onClick={() => setFilter('booked')}
        >
          Booked
        </button>
        <button 
          className={`${styles['filter-btn']} ${filter === 'available' ? styles.active : ''}`}
          onClick={() => setFilter('available')}
        >
          Available
        </button>
        <button 
          className={`${styles['filter-btn']} ${filter === 'byDate' ? styles.active : ''}`}
          onClick={() => {
            setFilter('byDate');
            setShowDatePicker(true);
          }}
        >
          By Date
        </button>
      </div>
  
      {filter === 'byDate' && (
        <div className={styles['date-picker-container']} onClick={handleCalendarClick}>
          <input
            id="date-picker-input"
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className={styles['date-picker']}
          
          />
       
        </div>
      )}
  
      <div className={styles['dates-list']}>
        {getFilteredDates().map(renderDateCard)}
      </div>
  
      {filter !== 'byDate' && displayDays < 365 && (
        <button 
          className={styles['load-more']}
          onClick={() => setDisplayDays(prev => prev + 30)}
        >
          Load More Dates
        </button>
      )}
    </div>
  );
};

// PhotoViewerModal Component
const PhotoViewerModal = ({ isOpen, onClose, photoUrl, photoType }) => {
  // Declare all hooks at the top level before any conditionals
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  
  // Prevent scroll on body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Handle swipe functions
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };
  
  const handleTouchEnd = () => {
    // If swiped down more than 100px, close the modal
    if (touchStart - touchEnd < -100) {
      onClose();
    }
    // Reset values
    setTouchStart(0);
    setTouchEnd(0);
  };
  
  // Handle click outside to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Return null after declaring all hooks
  if (!isOpen) return null;

  return (
    <div 
      className={styles.photoModalOverlay} 
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.photoModalContent}>
        <button 
          className={styles.photoModalClose}
          onClick={onClose}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <div className={styles.photoContainer}>
          <img 
            src={photoUrl} 
            alt={`${photoType === 'profile' ? 'Profile' : 'Cover'} photo`} 
            className={
              photoType === 'profile' 
                ? styles.enlargedProfilePhoto 
                : styles.enlargedCoverPhoto
            }
          />
        </div>
        
        <div className={styles.photoInfo}>
          <p>
            {photoType === 'profile' 
              ? 'Profile Photo' 
              : 'Cover Photo'
            }
          </p>
        </div>
      </div>
    </div>
  );
};


// Main Profile Component
const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};
  const { modalState, openPostModal, getCachedPosts, cachePosts, clearUserPostsCache, removePost } = usePostContext();
  const { userData: currentUserData, getUserDataFromCache, getProfileData, getRecommendationStatus, updateRecommendationCache, authToken  } = useContext(UserContext);
  const { events, setEvents, selectedEvent: contextSelectedEvent } = useContext(TourContext);
  const [profileData, setProfileData] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('dates'); // Default to dates since showcase is commented out
  const [displayedPosts, setDisplayedPosts] = useState(9);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const loadingMoreRef = useRef(false);  
  const [selectedTag, setSelectedTag] = useState('all');
  const [userTags, setUserTags] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [showProfileShareModal, setShowProfileShareModal] = useState(false);
  const [isPostsLoading, setIsPostsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [recommendationsModalVisible, setRecommendationsModalVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toursModalVisible, setToursModalVisible] = useState(false);
  const [showsModalVisible, setShowsModalVisible] = useState(false);
  const [usersModalVisible, setUsersModalVisible] = useState(false);
  const [hasRecommended, setHasRecommended] = useState(false);
  const [showAddOnTourModal, setShowAddOnTourModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);  
  const [showBrandsModal, setShowBrandsModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isRequestingSent, setIsRequestingSent] = useState(false);
  const [showRecommendationDisabledModal, setShowRecommendationDisabledModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [sharePostData, setSharePostData] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPostReportModal, setShowPostReportModal] = useState(false);
  const [postToReport, setPostToReport] = useState(null);
  const [isConnectingLoading, setIsConnectingLoading] = useState(false);
  const [connectionsModalVisible, setConnectionsModalVisible] = useState(false);
  const [showDisconnectConfirmModal, setShowDisconnectConfirmModal] = useState(false);
  const [showUnrecommendConfirmModal, setShowUnrecommendConfirmModal] = useState(false);
  const [connectionRequestSent, setConnectionRequestSent] = useState(false);
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [showMessageRestrictionModal, setShowMessageRestrictionModal] = useState(false);
  const [showEmailPermissionModal, setShowEmailPermissionModal] = useState(false);
  const [emailPermissionMessage, setEmailPermissionMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [isRecommendationInProgress, setIsRecommendationInProgress] = useState(false);
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const [showEPKModal, setShowEPKModal] = useState(false);
  const [IsEpkUploading, setIsEpkUploading] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const [showEPKBuilder, setShowEPKBuilder] = useState(false);
  const [epkBuilderMode, setEPKBuilderMode] = useState('create'); // 'create', 'edit', or 'view'
  const [customEPK, setCustomEPK] = useState(null);

   const [showPhotoModal, setShowPhotoModal] = useState(false);
 const [photoToView, setPhotoToView] = useState(null);

  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: '', // 'success', 'error', 'warning', 'info'
    onConfirm: null,
    showCancel: false
  });

  // Event handlers
  const handleToursCountClick = () => setToursModalVisible(true);
  const handleShowsCountClick = () => setShowsModalVisible(true);
  const handleUsersCountClick = () => setUsersModalVisible(true);
  const handleRecommendationsClick = () => setRecommendationsModalVisible(true);

  // Fetch user events (tours & shows) function - separate to avoid nesting
  const fetchUserEvents = async (userId) => {
    try {
      const db = getFirestoreInstance();
      
      // Query the assignedEvents subcollection
      const assignedEventsRef = collection(db, 'users', userId, 'assignedEvents');
      const assignedEventsSnapshot = await getDocs(assignedEventsRef);
      
      if (!assignedEventsSnapshot.empty) {
        const eventsData = [];
        
        // Process each assigned event (tour or show)
        for (const eventDoc of assignedEventsSnapshot.docs) {
          const eventData = eventDoc.data();
          const eventType = eventData.type || 'tour';
          const eventId = eventType === 'tour' ? eventData.tourId : eventData.showId;
          const collectionName = eventType === 'tour' ? 'tours' : 'shows';
          
          if (!eventId) continue;
          
          try {
            // Get the main event document
            const mainEventDoc = await getDoc(doc(db, collectionName, eventId));
            if (!mainEventDoc.exists()) continue;
            
            const mainEventData = mainEventDoc.data();
            
            // Get event dates from eventDates subcollection
            const datesSnapshot = await getDocs(
              collection(db, collectionName, eventId, 'eventDates')
            );
            
            const eventDates = datesSnapshot.docs.map(dateDoc => ({
              id: dateDoc.id,
              ...dateDoc.data(),
            }));
            
            // Get assignedUsers from assignedUsers subcollection
            const assignedUsersSnapshot = await getDocs(
              collection(db, collectionName, eventId, 'assignedUsers')
            );
            
            const assignedUsers = assignedUsersSnapshot.docs.map(userDoc => ({
              userId: userDoc.id.replace('band_', ''), // Handle band IDs that may have prefix
              ...userDoc.data(),
            }));
            
            // Get pendingInvitations from pendingInvitations subcollection
            const pendingInvitationsSnapshot = await getDocs(
              collection(db, collectionName, eventId, 'pendingInvitations')
            );
            
            const pendingInvitations = pendingInvitationsSnapshot.docs.map(invDoc => ({
              id: invDoc.id,
              ...invDoc.data(),
            }));
            
            eventsData.push({
              id: eventId,
              type: eventType,
              ...mainEventData,
              // Include both new and old naming for maximum compatibility
              eventDates: eventDates, // New field name
              assignedDates: eventData.assignedDates || [],
              assignedUsers: assignedUsers,
              pendingInvitations: pendingInvitations
            });
          } catch (eventError) {
            console.error(`Error fetching event ${eventId}:`, eventError);
            // Continue with other events even if one fails
          }
        }
        
        return eventsData;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Failed to load events:", error);
      return [];
    }
  };

  // Fetch profile data function
  const fetchProfileData = async () => {
    // Skip fetching if we're viewing a post or if a post modal is open
    if (location.pathname.startsWith('/p/') || modalState.isOpen) {
      return;
    }
    
    // Always set loading state to true when username changes
    setIsLoading(true);
    setIsPageLoading(true);
    
    try {
      // Case 1: Viewing your own profile
      if (currentUserData?.userName === username) {
        console.log("Loading own profile");
        setProfileData(currentUserData);
        setIsOwnProfile(true);
        setIsLoading(false);
        setIsPageLoading(false);
  
        setDefaultActiveTab(currentUserData.userType);
        
        // Even for own profile, fetch events in the background
        fetchUserEvents(currentUserData.userId).then(eventsData => {
          setEvents(eventsData);
        }).catch(error => {
          console.error("Failed to load events for own profile:", error);
          setEvents([]);
        });
        
        return;
      }
      
      // Case 2: Viewing someone else's profile
      console.log("Loading other user profile:", username);
      
      // First check if we have this user's ID from their username
      const db = getFirestore();
      const userQuery = query(collection(db, 'users'), where('userName', '==', username));
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userId = userSnapshot.docs[0].id;
        
        // Get profile data using getProfileData which won't modify context
        const profileUserData = await getProfileData(userId);
        
        // IMPORTANT: Explicitly fetch the latest recommendation count from Firestore
        // This ensures we have accurate count even if cached data is stale
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Update the recommendation count with fresh data
            profileUserData.recommendationsCount = userData.recommendationsCount || 0;
          }
        } catch (err) {
          console.error("Error fetching fresh recommendation count:", err);
        }
        
        // Set to local state (not global context)
        setProfileData(profileUserData);
        setIsOwnProfile(false);
        
        // End the loading state for the profile early
        setIsLoading(false);
        setIsPageLoading(false);
  
        setDefaultActiveTab(profileUserData.userType);
        
        // Start fetching events in the background - don't block profile display
        fetchUserEvents(userId).then(eventsData => {
          setEvents(eventsData);
        }).catch(error => {
          console.error("Failed to load events:", error);
          setEvents([]);
        });
        
      } else {
        console.log("User not found");
        setProfileData(null);  // Set to null to indicate user not found
        setIsLoading(false);
        setIsPageLoading(false);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileData(null);
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (profileData?.userId) {
      // Check and update experience years when profile data is loaded
      checkAndUpdateExperienceYears(profileData);
    }
  }, [profileData?.userId]);
  

  // useEffect for fetching profile data
  useEffect(() => {
    if (username) {
      // Only fetch if username actually changed or if we don't have profile data
      if (!profileData || profileData.userName !== username) {
        console.log("Username changed, fetching profile data:", username);
        fetchProfileData();
      }
    }
  }, [username, currentUserData, getProfileData, location.pathname, modalState.isOpen]);

  // Check if a user is booked for an event
  const isUserBooked = (events, userData) => {
    if (!events || !Array.isArray(events) || events.length === 0 || !userData?.userId) {
      return false;
    }
    
    // Format today's date in YYYY-MM-DD
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const formatToDateString = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Check each event (tour or show)
    for (const event of events) {
      // Skip if no assignedUsers data
      if (!event.assignedUsers) continue;
      
      // Find this user in the assignedUsers array
      const userAssignment = event.assignedUsers.find(user => 
        user.userId === userData.userId
      );
      
      // Skip if user not assigned to this event
      if (!userAssignment || !userAssignment.assignedDates) continue;
      
      // Check user's assigned dates
      for (const assignedDate of userAssignment.assignedDates) {
        // Find the actual date object from eventDates array
        const eventDate = event.eventDates?.find(ed => 
          ed.id === assignedDate.dateDocId
        );
        
        // Skip if date not found or cancelled
        if (!eventDate || eventDate.cancelled) continue;

        // Check both original and rescheduled dates
        const originalDate = formatToDateString(eventDate.date);
        const hasReschedule = eventDate.rescheduleDate?.isRescheduled;
        const rescheduledDate = hasReschedule ? 
          formatToDateString(eventDate.rescheduleDate.newDate) : null;

        // Match if either the original date (when not rescheduled) or 
        // the rescheduled date matches today
        if ((originalDate === todayString && !hasReschedule) || 
            (hasReschedule && rescheduledDate === todayString)) {
          return true;
        }
      }
    }

    return false;
  };

  // Handle URL parameters for scrolling to sections
useEffect(() => {
  // Check URL parameters for section and id
  const queryParams = new URLSearchParams(location.search);
  const section = queryParams.get('section');
  const id = queryParams.get('id');
  
  if (section) {
    // Set the active tab based on the section parameter
    if (section === 'reviews' || section === 'feedback') {
      setActiveTab(section);
      
      // After tab change, scroll to the section
      setTimeout(() => {
        // Find the content-section element
        const contentSection = document.querySelector(`.${styles['content-section']}`);
        
        if (contentSection) {
          // Scroll the content section into view with smooth behavior
          contentSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          
          // Add a highlight effect to draw attention to the active tab
          const activeTabElement = document.querySelector(`.${styles.tab}.${styles.active}`);
          if (activeTabElement) {
            // Add temporary highlight class
            activeTabElement.style.transition = 'background-color 0.5s ease';
            activeTabElement.style.backgroundColor = 'rgba(226, 56, 77, 0.1)';
            
            // Remove highlight after animation completes
            setTimeout(() => {
              activeTabElement.style.backgroundColor = '';
            }, 1500);
          }
          
          // If an ID is provided, attempt to scroll to that specific feedback/review
          if (id) {
            setTimeout(() => {
              const elementId = section === 'reviews' ? `review-${id}` : `feedback-${id}`;
              const targetElement = document.getElementById(elementId);
              
              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
                
                // Add highlight effect
                targetElement.style.transition = 'background-color 0.5s ease';
                targetElement.style.backgroundColor = 'rgba(226, 56, 77, 0.1)';
                
                setTimeout(() => {
                  targetElement.style.backgroundColor = '';
                }, 2000);
              }
            }, 500); // Delay to ensure content has loaded
          }
        }
      }, 200); // Short delay to ensure tab change has happened
    }
  }
}, [location.search]);


  const canViewPrivateContent = () => {
    // Always allow profile owner to view content
    if (isOwnProfile) return true;
    
    // If account is not private, anyone can view
    if (profileData?.accountVisibility !== 'Private') return true;
    
    // For private accounts, allow connections to view
    return isConnected;
  };

  const handleConnectionsClick = () => {
    // Always allow viewing own connections
    if (isOwnProfile) {
      setConnectionsModalVisible(true);
      return;
    }

    // Check connections privacy setting
    switch (profileData?.connectionsPrivacy) {
      case 'public':
        // Public - anyone can view
        setConnectionsModalVisible(true);
        break;
      
      case 'connections':
        // Only connections can view
        if (isConnected) {
          setConnectionsModalVisible(true);
        } else {
          showCustomAlert({
            title: "Connections Only",
            message: "This user's connections are only visible to their connections.",
            type: "info"
          });
        }
        break;
      
      case 'private':
      default:
        // Fully private or unspecified - nobody can view
        showCustomAlert({
          title: "Private Connections",
          message: "This user's connections are private.",
          type: "info"
        });
        break;
    }
  };

  // Check connection status
  useEffect(() => {
    // Only check when viewing someone else's profile
    if (!isOwnProfile && profileData?.userId && currentUserData?.userId) {
      const checkConnectionStatus = async () => {
        try {
          const db = getFirestoreInstance();
          
          // Check if users are already connected
          const connectionsRef = collection(db, 'users', currentUserData.userId, 'connections');
          const connectionsSnapshot = await getDocs(connectionsRef);
          
          let isUserConnected = false;
          for (const docSnapshot of connectionsSnapshot.docs) {
            const data = docSnapshot.data();
            if (data.connectedWith && data.connectedWith.includes(profileData.userId)) {
              isUserConnected = true;
              break;
            }
          }
          
          setIsConnected(isUserConnected);
          
          // If not connected, check if a request is pending
          if (!isUserConnected) {
            // Check if user has sent a connection request that's pending
            const connectionRequestRef = doc(db, 'users', profileData.userId, 'connectionRequests', currentUserData.userId);
            const connectionRequestDoc = await getDoc(connectionRequestRef);
            
            if (connectionRequestDoc.exists()) {
              const requestData = connectionRequestDoc.data();
              if (requestData.status === 'pending') {
                setConnectionRequestSent(true);
              }
            } else {
              setConnectionRequestSent(false);
            }
          } else {
            // If connected, ensure request state is false
            setConnectionRequestSent(false);
          }
        } catch (error) {
          console.error('Error checking connection status:', error);
        }
      };
      
      checkConnectionStatus();
    }
  }, [isOwnProfile, profileData?.userId, currentUserData?.userId]);

  // Check recommendation status
  useEffect(() => {
    // Only check recommendations status when viewing someone else's profile
    // and we have both the profile data and current user data
    if (!isOwnProfile && profileData?.userId && currentUserData?.userId) {
      const checkStatus = async () => {
        try {
          const status = await getRecommendationStatus(
            currentUserData.userId, 
            profileData.userId
          );
          setHasRecommended(status);
        } catch (error) {
          console.error('Error checking recommendation status:', error);
        }
      };
      
      checkStatus();
    }
  }, [isOwnProfile, profileData?.userId, currentUserData?.userId, getRecommendationStatus]);

  // Functions for handling user actions
  const handleUserSelect = async (user) => {
    setIsLoading(true);
    setIsPageLoading(true);
    
    if (user.userId !== currentUserData?.userId) {
      try {
        await getProfileData(user.userId);
        console.log("Profile data pre-fetched successfully");
      } catch (error) {
        console.error("Failed to pre-fetch profile data:", error);
      }
    }
    
    navigate(`/${user.userName}`);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
  };

  const handleShareProfile = async () => {
    const shareData = {
      title: `${profileData?.fullName}'s Profile on OnTour`,
      text: profileData?.bio || 'Check out this profile on OnTour',
      url: window.location.href
    };
  
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
    if (isMobileDevice && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          handleClipboardCopy(window.location.href);
        }
      }
    } else {
      setShowProfileShareModal(true);
    }
  };

    // Helper function to check if all social links are present
    const isAllSocialLinksPresent = (socialLinks) => {
      if (!socialLinks) return false;
      
      // Define all possible social links
      const allSocialLinks = [
        'instagramUrl',
        'twitterUrl',
        'linkedInUrl',
        'facebookUrl',
        'youtubeUrl',
        'spotifyUrl',
        'appleMusicUrl'
      ];
      
      // Check if all links are present and not empty
      return allSocialLinks.every(link => socialLinks[link] && socialLinks[link].trim() !== '');
    };
  

  const showCustomAlert = (config) => {
    setAlertConfig(config);
    setShowAlert(true);
  };

  const isMobileDevice = () => {
    return (
      typeof window !== 'undefined' && 
      (window.innerWidth <= 768 || 'ontouchstart' in window)
    );
  };

  const isAddToEventAllowed = () => {
    if (currentUserData?.userType === 'Manager') {
      if (profileData.allowManagers === 'True' && profileData.allowedManagers) {
        return profileData.allowedManagers.some(manager => manager.userId === currentUserData.userId);
      }
      return true;
    }
    return false;
  };

  const checkAndUpdateExperienceYears = async (userData) => {
    if (!userData || !userData.userId) return;
    
    // Skip if user doesn't have experience years data
    if (!userData.startedLearningYear && !userData.startedProfessionalYear) return;
    
    try {
      // Get cached experience data for this user
      const cachedData = getCachedExperienceYears(userData.userId);
      
      // Get current year (with improved caching and verification)
      const currentYear = await getCurrentYear();
      
      // If we have cached data, check if it needs updating
      if (cachedData) {
        // Check if we need to update experience years
        const needsUpdate = shouldUpdateExperienceYears(cachedData, userData, currentYear);
        
        if (!needsUpdate) {
          // No update needed, but ensure profileData has the latest values
          if (userData.overallExperienceYears !== cachedData.overallExperienceYears ||
              userData.professionalExperienceYears !== cachedData.professionalExperienceYears) {
            // Update local state with cached values if they're different
            // This is just UI update, not Firestore update
            setProfileData(prev => ({
              ...prev,
              overallExperienceYears: cachedData.overallExperienceYears,
              professionalExperienceYears: cachedData.professionalExperienceYears
            }));
          }
          
          return;
        }
      }
      
      // Calculate new experience years
      const experienceData = calculateExperienceYears(userData, currentYear);
      
      // Check if the calculated values are different from the stored values
      if (experienceData.overallExperienceYears !== userData.overallExperienceYears ||
          experienceData.professionalExperienceYears !== userData.professionalExperienceYears) {
        
        // Only update Firestore if current user is viewing their own profile or if they're an admin
        const isOwnProfileOrAdmin = isOwnProfile || currentUserData?.isAdmin;
        
        if (isOwnProfileOrAdmin) {
          // Update in Firestore
          const updated = await updateExperienceYearsInFirestore(
            userData.userId, 
            experienceData
          );
          
          if (updated) {
            console.log('Experience years updated in Firestore');
          }
        }
        
        // Update local state regardless of Firestore update
        setProfileData(prev => ({
          ...prev,
          overallExperienceYears: experienceData.overallExperienceYears,
          professionalExperienceYears: experienceData.professionalExperienceYears
        }));
      }
      
      // Always cache the calculated values
      cacheExperienceYears(userData.userId, experienceData);
      
    } catch (error) {
      console.error('Error checking and updating experience years:', error);
    }
  };

  // Handle connection and recommendation actions
  const toggleConnection = async () => {
    if (isConnected) {
      setShowDisconnectConfirmModal(true);
      return;
    }
    
    if (connectionRequestSent) {
      setShowCancelRequestModal(true);
      return;
    }
    
    try {
      setIsConnectingLoading(true);
      const db = getFirestoreInstance();
      
      const requestData = {
        senderUserId: currentUserData.userId,
        senderUserName: currentUserData.userName,
        senderFullName: currentUserData.fullName,
        senderProfilePictureUrl: currentUserData.profilePictureUrl || '',
        senderUserType: currentUserData.userType,
        senderUserSubType: currentUserData.userSubType,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      const notificationData = {
        type: 'CONNECTION_REQUEST',
        title: 'Connection Request',
        message: `${currentUserData.fullName || currentUserData.userName} wants to connect with you.`,
        data: {
          senderUserId: currentUserData.userId,
          _displayData: {
            senderUserName: currentUserData.userName,
            senderFullName: currentUserData.fullName,
            senderProfilePictureUrl: currentUserData.profilePictureUrl || '',
          }
        },
        status: 'pending'
      };
      
      await createNotification(profileData.userId, 'general', notificationData, currentUserData.userId);
      
      const connectionRequestRef = doc(db, 'users', profileData.userId, 'connectionRequests', currentUserData.userId);
      await setDoc(connectionRequestRef, requestData);
      
      setConnectionRequestSent(true);
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert('Failed to send connection request. Please try again.');
    } finally {
      setIsConnectingLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    try {
      setIsConnectingLoading(true);
      const db = getFirestoreInstance();
      
      const connectionRequestRef = doc(db, 'users', profileData.userId, 'connectionRequests', currentUserData.userId);
      await deleteDoc(connectionRequestRef);
      
      const chunksSnapshot = await getDocs(collection(db, 'users', profileData.userId, 'notifications'));
      
      for (const chunkDoc of chunksSnapshot.docs) {
        const chunkData = chunkDoc.data();
        const notifications = chunkData.notifications || [];
        
        const notificationIndex = notifications.findIndex(n => 
          n.type === 'CONNECTION_REQUEST' && 
          n.data?.senderUserId === currentUserData.userId
        );
        
        if (notificationIndex !== -1) {
          const updatedNotifications = [...notifications];
          updatedNotifications.splice(notificationIndex, 1);
          
          await updateDoc(chunkDoc.ref, {
            notifications: updatedNotifications
          });
          
          break;
        }
      }
      
      setConnectionRequestSent(false);
      setShowCancelRequestModal(false);
      
    } catch (error) {
      console.error('Error cancelling connection request:', error);
      alert('Failed to cancel request. Please try again.');
    } finally {
      setIsConnectingLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsConnectingLoading(true);
      const db = getFirestoreInstance();
      
      let docToUpdate = null;
      const connectionsRef = collection(db, 'users', currentUserData.userId, 'connections');
      const connectionsSnapshot = await getDocs(connectionsRef);
      
      for (const docSnapshot of connectionsSnapshot.docs) {
        const data = docSnapshot.data();
        if (data.connectedWith && data.connectedWith.includes(profileData.userId)) {
          docToUpdate = docSnapshot.ref;
          break;
        }
      }
      
      if (docToUpdate) {
        await updateDoc(docToUpdate, {
          connectedWith: arrayRemove(profileData.userId),
          updatedAt: new Date()
        });
        
        await updateDoc(doc(db, 'users', currentUserData.userId), {
          connectionsCount: increment(-1)
        });
        
        let otherUserDocToUpdate = null;
        const otherUserConnectionsRef = collection(db, 'users', profileData.userId, 'connections');
        const otherUserConnectionsSnapshot = await getDocs(otherUserConnectionsRef);
        
        for (const docSnapshot of otherUserConnectionsSnapshot.docs) {
          const data = docSnapshot.data();
          if (data.connectedWith && data.connectedWith.includes(currentUserData.userId)) {
            otherUserDocToUpdate = docSnapshot.ref;
            break;
          }
        }
        
        if (otherUserDocToUpdate) {
          await updateDoc(otherUserDocToUpdate, {
            connectedWith: arrayRemove(currentUserData.userId),
            updatedAt: new Date()
          });
          
          await updateDoc(doc(db, 'users', profileData.userId), {
            connectionsCount: increment(-1)
          });
        }
        
        setIsConnected(false);
        setProfileData(prev => ({
          ...prev,
          connectionsCount: Math.max((prev.connectionsCount || 0) - 1, 0)
        }));
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setIsConnectingLoading(false);
      setShowDisconnectConfirmModal(false);
    }
  };

  const handleUnrecommend = async () => {
    // Prevent multiple rapid clicks
    if (isRecommendationInProgress) return;
    
    try {
      setIsRecommendationInProgress(true);
      const db = getFirestoreInstance();
      
      let docToUpdate = null;
      const recsRef = collection(db, 'users', profileData.userId, 'recommendations');
      const recsSnapshot = await getDocs(recsRef);
      
      for (const docSnapshot of recsSnapshot.docs) {
        const data = docSnapshot.data();
        if (data.recommendedBy && data.recommendedBy.includes(currentUserData.userId)) {
          docToUpdate = docSnapshot.ref;
          break;
        }
      }
      
      if (docToUpdate) {
        await updateDoc(docToUpdate, {
          recommendedBy: arrayRemove(currentUserData.userId),
          updatedAt: new Date()
        });
        
        await updateDoc(doc(db, 'users', profileData.userId), {
          recommendationsCount: increment(-1)
        });
        
        updateRecommendationCache(currentUserData.userId, profileData.userId, false);
        
        setHasRecommended(false);
        setProfileData(prev => ({
          ...prev,
          recommendationsCount: Math.max((prev.recommendationsCount || 0) - 1, 0)
        }));
      }
    } catch (error) {
      console.error('Error removing recommendation:', error);
      alert('Failed to remove recommendation. Please try again.');
    } finally {
      setIsRecommendationInProgress(false);
      setShowUnrecommendConfirmModal(false);
    }
  };

  const toggleRecommendation = async () => {
    // Prevent multiple rapid clicks
    if (isRecommendationInProgress) return;
    
    try {
      setIsRecommendationInProgress(true);
      const db = getFirestoreInstance();
      
      if (hasRecommended) {
        // Remove recommendation
        let docToUpdate = null;
        const recsRef = collection(db, 'users', profileData.userId, 'recommendations');
        const recsSnapshot = await getDocs(recsRef);
        
        // Find which document contains this user's recommendation
        for (const docSnapshot of recsSnapshot.docs) {
          const data = docSnapshot.data();
          if (data.recommendedBy && data.recommendedBy.includes(currentUserData.userId)) {
            docToUpdate = docSnapshot.ref;
            break;
          }
        }
        
        if (docToUpdate) {
          // Use arrayRemove to remove the user's ID
          await updateDoc(docToUpdate, {
            recommendedBy: arrayRemove(currentUserData.userId),
            updatedAt: new Date()
          });
          
          // Decrement the recommendations count
          await updateDoc(doc(db, 'users', profileData.userId), {
            recommendationsCount: increment(-1)
          });
          
          // Update cache immediately
          updateRecommendationCache(currentUserData.userId, profileData.userId, false);
          
          // Update local state - OPTIMISTIC UI UPDATE
          setHasRecommended(false);
          setProfileData(prev => ({
            ...prev,
            recommendationsCount: Math.max((prev.recommendationsCount || 0) - 1, 0)
          }));
        }
      } else {
        // Add recommendation
        // Get all recommendation chunk documents
        const recsRef = collection(db, 'users', profileData.userId, 'recommendations');
        const recsSnapshot = await getDocs(recsRef);
        
        let docToUpdate = null;
        let highestChunk = 0;
        
        // Find highest chunk number
        for (const docSnapshot of recsSnapshot.docs) {
          const docId = docSnapshot.id;
          
          // Extract chunk number
          if (docId.includes('_chunk')) {
            const chunkNum = parseInt(docId.split('_chunk')[1]);
            if (!isNaN(chunkNum) && chunkNum > highestChunk) {
              highestChunk = chunkNum;
            }
          }
          
          // Check if this document has space
          const data = docSnapshot.data();
          if (data.recommendedBy && data.recommendedBy.length < 1000) {
            docToUpdate = docSnapshot.ref;
          }
        }
        
        // If no suitable document found, create a new one with proper naming
        if (!docToUpdate) {
          const newChunkNum = highestChunk + 1;
          const newDocId = `recommendations_chunk${newChunkNum}`;
          docToUpdate = doc(db, 'users', profileData.userId, 'recommendations', newDocId);
          
          await setDoc(docToUpdate, {
            recommendedBy: [],
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
        
        // Add the recommendation
        await updateDoc(docToUpdate, {
          recommendedBy: arrayUnion(currentUserData.userId),
          updatedAt: new Date()
        });
        
        // Increment the recommendations count
        await updateDoc(doc(db, 'users', profileData.userId), {
          recommendationsCount: increment(1)
        });
        
        // Update cache immediately
        updateRecommendationCache(currentUserData.userId, profileData.userId, true);
        
        // Update local state - OPTIMISTIC UI UPDATE
        setHasRecommended(true);
        setProfileData(prev => ({
          ...prev,
          recommendationsCount: (prev.recommendationsCount || 0) + 1
        }));
      }
    } catch (error) {
      console.error('Error toggling recommendation:', error);
    } finally {
      setIsRecommendationInProgress(false);
    }
  };

  const handleRecommendationRequest = async () => {
    // Check membership tier
    if (currentUserData?.membershipTier !== 'Elite') {
      setShowUpgradeModal(true);
      return;
    }
    
    // Check if the user has disabled recommendation requests
    if (profileData?.disableRecommendationRequests) {
      setShowRecommendationDisabledModal(true);
      return;
    }
    
    setIsRequestingSent(true);
    
    try {
      // Check if in cooldown first
      const cooldownStatus = await checkRecommendationCooldown(
        currentUserData.userId,
        profileData.userId
      );
      
      if (cooldownStatus.inCooldown) {
        const timeRemaining = formatCooldownTimeRemaining(cooldownStatus.cooldownEndDate);
        setShowCustomModal({
          title: 'Cooldown Period',
          message: `You need to wait ${timeRemaining} before requesting another recommendation from this user.`,
          onClose: () => setShowCustomModal(null)
        });
        return;
      }
      
      // Prepare sender data - only include necessary info
      const senderData = {
        userId: currentUserData.userId,
        userName: currentUserData.userName,
        fullName: currentUserData.fullName,
        profilePictureUrl: currentUserData.profilePictureUrl || ''
      };
      
      // Send the recommendation request using the new service
      const success = await sendRecommendationRequestNotification(
        profileData.userId, 
        senderData,
        currentUserData.userId
      );
      
      // Also store it in our recommendation tracking system
      await sendRecommendationRequest(
        currentUserData.userId,
        currentUserData,
        profileData.userId
      );
      
      if (success) {
        setShowCustomModal({
          title: 'Success',
          message: 'Recommendation request sent!',
          onClose: () => setShowCustomModal(null)
        });
      } else {
        setShowCustomModal({
          title: 'Error',
          message: 'Failed to send recommendation request. Please try again.',
          onClose: () => setShowCustomModal(null)
        });
      }
    } catch (error) {
      console.error('Error sending recommendation request:', error);
      setShowCustomModal({
        title: 'Error',
        message: error.message || 'Failed to send recommendation request. Please try again.',
        onClose: () => setShowCustomModal(null)
      });
    } finally {
      setIsRequestingSent(false);
    }
  };

  const isEventInCurrentMonth = (event) => {
    if (!event || !event.eventDates || event.eventDates.length === 0) {
      return false;
    }
  
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
  
    return event.eventDates.some(date => {
      // Check both original date and rescheduled date if it exists
      const originalDate = new Date(date.date);
      
      if (originalDate.getMonth() === currentMonth && 
          originalDate.getFullYear() === currentYear && 
          !date.cancelled) {
        return true;
      }
      
      // Check rescheduled date if it exists
      if (date.rescheduleDate?.isRescheduled) {
        const rescheduledDate = new Date(date.rescheduleDate.newDate);
        return rescheduledDate.getMonth() === currentMonth && 
               rescheduledDate.getFullYear() === currentYear && 
               !date.cancelled;
      }
      
      return false;
    });
  };


 const AssociatedActsSection = ({ profileData, isOwnProfile, navigateToUserProfile, showCustomAlert }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [displayedCurrentActs, setDisplayedCurrentActs] = useState([]);
  const [displayedPastActs, setDisplayedPastActs] = useState([]);
  const [pendingCurrentActs, setPendingCurrentActs] = useState([]);
  const [pendingPastActs, setPendingPastActs] = useState([]);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Track initialization state
  
  // Initialize data from profileData
  useEffect(() => {
    if (profileData?.userId) {
      // Set displayed acts from the arrays in profileData - filter out any with isPending flag
      const currentActs = profileData.associatedActs || [];
      const pastActs = profileData.pastAssociatedActs || [];
      
      // For non-owner, only show confirmed acts
      if (!isOwnProfile) {
        setDisplayedCurrentActs(currentActs.filter(act => !act.isPending));
        setDisplayedPastActs(pastActs.filter(act => !act.isPending));
      } else {
        // For owner, show all acts and separate pending from confirmed
        setDisplayedCurrentActs(currentActs.filter(act => !act.isPending));
        setDisplayedPastActs(pastActs.filter(act => !act.isPending));
        setPendingCurrentActs(currentActs.filter(act => act.isPending));
        setPendingPastActs(pastActs.filter(act => act.isPending));
        
        // Also fetch any pending requests from subcollection
        fetchPendingRequests();
      }
      
      // Use counter fields to determine if there are more items
      setHasMoreCurrent((profileData.associatedActsCount || 0) > 3);
      setHasMorePast((profileData.pastAssociatedActsCount || 0) > 3);
      
      // Mark initialization as complete
      setIsInitialized(true);
    }
  }, [profileData, isOwnProfile]);
  
  // Fetch pending requests from subcollection
  const fetchPendingRequests = async () => {
    if (!profileData?.userId) return;
    
    try {
      setIsLoading(true);
      const db = getFirestoreInstance();
      
      const pendingRequestsRef = collection(db, 'users', profileData.userId, 'pendingAssociationRequests');
      const requestsSnapshot = await getDocs(pendingRequestsRef);
      
      if (!requestsSnapshot.empty) {
        // Create arrays for current and past requests
        const currentRequests = [];
        const pastRequests = [];
        
        requestsSnapshot.docs.forEach(doc => {
          const requestData = {
            ...doc.data(),
            docId: doc.id,
            docRef: doc.ref,
            isPending: true
          };
          
          // Sort into current or past based on isPast flag
          if (requestData.isPast) {
            pastRequests.push(requestData);
          } else {
            currentRequests.push(requestData);
          }
        });
        
        // Update state with pending requests - avoid duplicates by checking associationId
        setPendingCurrentActs(prev => {
          const existingIds = new Set(prev.map(act => act.associationId));
          const newPendingActs = currentRequests.filter(req => !existingIds.has(req.associationId));
          return [...prev, ...newPendingActs];
        });
        
        setPendingPastActs(prev => {
          const existingIds = new Set(prev.map(act => act.associationId));
          const newPendingActs = pastRequests.filter(req => !existingIds.has(req.associationId));
          return [...prev, ...newPendingActs];
        });
      }
    } catch (error) {
      console.error('Error fetching pending association requests:', error);
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Error',
          message: 'Failed to load pending association requests.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // If still initializing, don't render anything yet to prevent flicker
  if (!isInitialized) {
    return null; // Or return a subtle loading placeholder if preferred
  }


  // Handle canceling a pending request
  const handleCancelPendingRequest = async (act) => {
    if (!act.docRef && !act.docId) return;
    
    try {
      setIsLoading(true);
      
      if (act.docRef) {
        await deleteDoc(act.docRef);
      } else {
        const db = getFirestoreInstance();
        await deleteDoc(doc(db, 'users', profileData.userId, 'pendingAssociationRequests', act.docId));
      }
      
      // Remove from UI
      if (act.isPast) {
        setPendingPastActs(prev => 
          prev.filter(item => item.associationId !== act.associationId)
        );
      } else {
        setPendingCurrentActs(prev => 
          prev.filter(item => item.associationId !== act.associationId)
        );
      }
      
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Success',
          message: 'Pending request canceled',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error canceling pending request:', error);
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Error',
          message: 'Failed to cancel the request',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more acts from subcollection
  const loadMoreActs = async (isPast = false) => {
    if (isLoading || !profileData?.userId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const actsRef = collection(db, 'users', profileData.userId, 'associatedActs');
      
      // Get the currently displayed acts
      const currentDisplayed = isPast ? displayedPastActs : displayedCurrentActs;
      
      // Skip the already displayed acts
      const displayedIds = currentDisplayed.map(act => act.id);
      const actsQuery = query(
        actsRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(currentDisplayed.length + 3) // Get all existing plus 3 more
      );
      
      const actsSnapshot = await getDocs(actsQuery);
      
      if (!actsSnapshot.empty) {
        const allActs = actsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out acts that are already displayed
        const newActs = allActs.filter(act => !displayedIds.includes(act.id));
        
        // Update the displayed acts
        if (isPast) {
          const updatedPastActs = [...displayedPastActs, ...newActs];
          setDisplayedPastActs(updatedPastActs);
          // Update the "hasMore" flag based on counter - only if there are more than what we have
          setHasMorePast((profileData.pastAssociatedActsCount || 0) > updatedPastActs.length);
        } else {
          const updatedCurrentActs = [...displayedCurrentActs, ...newActs];
          setDisplayedCurrentActs(updatedCurrentActs);
          // Update the "hasMore" flag based on counter - only if there are more than what we have
          setHasMoreCurrent((profileData.associatedActsCount || 0) > updatedCurrentActs.length);
        }
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} acts:`, error);
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Error',
          message: `Failed to load more ${isPast ? 'past' : 'current'} associated acts.`,
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  // Format "since" date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    // Calculate years and months since
    const now = new Date();
    const startDate = new Date(parseInt(year), parseInt(month) - 1);
    
    let yearsSince = now.getFullYear() - startDate.getFullYear();
    let monthsSince = now.getMonth() - startDate.getMonth();
    
    if (monthsSince < 0) {
      yearsSince--;
      monthsSince += 12;
    }
    
    let sinceText = '';
    if (yearsSince > 0) {
      sinceText += `${yearsSince} year${yearsSince !== 1 ? 's' : ''}`;
      if (monthsSince > 0) {
        sinceText += `, ${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
      }
    } else if (monthsSince > 0) {
      sinceText = `${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
    } else {
      sinceText = 'This month';
    }
    
    return `Since ${monthName} ${year} (${sinceText})`;
  };

 // Render an associated act card
  const renderActCard = (act, isPending = false) => {
    return (
      <div 
        key={act.associationId || `${act.type}-${act.id}`} 
        className={`${styles.actCard} ${isPending ? styles.pendingCard : ''}`}
        onClick={isPending ? null : () => act.type === 'band' 
          ? navigate(`/bands/${act.username}`) 
          : navigateToUserProfile(act.username)
        }
      >
        <img 
          src={act.photoUrl || defaultProfilePhoto} 
          alt={act.name} 
          className={styles.actPhoto}
        />
        <div className={styles.actInfo}>
          <div className={styles.actNameContainer}>
            <h4>{act.name}</h4>
            {act.verified && (
              <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
            )}
          </div>
          <p className={styles.actUsername}>@{act.username}</p>
          
          {/* Display role if available */}
          {act.role && (
            <div className={styles.roleBadge}>
              <FontAwesomeIcon icon={faUserShield} />
              <span>as a {act.role}</span>
            </div>
          )}
          
          {/* Display since date if available (for current acts) */}
          {!act.isPast && act.since && (
            <div className={styles.sinceBadge}>
              <FontAwesomeIcon icon={faClock} />
              <span>{formatSince(act.since)}</span>
            </div>
          )}
          
          {/* Display duration if available (for past acts) */}
          {act.isPast && act.duration && (
            <div className={styles.durationBadge}>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{formatDuration(act.duration)}</span>
            </div>
          )}
          
          {/* Display pending badge */}
          {isPending && (
            <div className={styles.pendingBadge}>
              <FontAwesomeIcon icon={faClock} /> Pending Approval
            </div>
          )}
        </div>
        
        {isPending && (
          <button 
            className={styles.cancelButton}
            onClick={(e) => {
              e.stopPropagation();
              handleCancelPendingRequest(act);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Associated Acts</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {/* Display current associated acts */}
      {activeTab === 'current' && (
        <>
          {(displayedCurrentActs.length > 0 || (isOwnProfile && pendingCurrentActs.length > 0)) ? (
            <>
              <div className={styles.associatedActsGrid}>
                {/* Render pending acts first (only for own profile) */}
                {isOwnProfile && pendingCurrentActs.map(act => 
                  renderActCard(act, true)
                )}
                
                {/* Render confirmed acts */}
                {displayedCurrentActs.map(act => 
                  renderActCard(act)
                )}
                
                {isOwnProfile && (
                  <button 
                    className={styles.addMoreActsButton}
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add More
                  </button>
                )}
              </div>
              
              {/* Load More button for current acts - only show if there are more than we're displaying */}
              {hasMoreCurrent && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreActs(false)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No current associated acts yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => navigate('/edit-profile')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Associated Acts
                </button>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Display past associated acts */}
      {activeTab === 'past' && (
        <>
          {(displayedPastActs.length > 0 || (isOwnProfile && pendingPastActs.length > 0)) ? (
            <>
              <div className={styles.associatedActsGrid}>
                {/* Render pending past acts first (only for own profile) */}
                {isOwnProfile && pendingPastActs.map(act => 
                  renderActCard(act, true)
                )}
                
                {/* Render confirmed past acts */}
                {displayedPastActs.map(act => 
                  renderActCard(act)
                )}
                
                {isOwnProfile && (
                  <button 
                    className={styles.addMoreActsButton}
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add More
                  </button>
                )}
              </div>
              
              {/* Load More button for past acts - only show if there are more than we're displaying */}
              {hasMorePast && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreActs(true)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No past associated acts yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => navigate('/edit-profile')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Past Associated Acts
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};


  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    // Calculate years and months since
    const now = new Date();
    const startDate = new Date(parseInt(year), parseInt(month) - 1);
    
    let yearsSince = now.getFullYear() - startDate.getFullYear();
    let monthsSince = now.getMonth() - startDate.getMonth();
    
    if (monthsSince < 0) {
      yearsSince--;
      monthsSince += 12;
    }
    
    let sinceText = '';
    if (yearsSince > 0) {
      sinceText += `${yearsSince} year${yearsSince !== 1 ? 's' : ''}`;
      if (monthsSince > 0) {
        sinceText += `, ${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
      }
    } else if (monthsSince > 0) {
      sinceText = `${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
    } else {
      sinceText = 'This month';
    }
    
    return `Since ${monthName} ${year} (${sinceText})`;
  };






 // EndorsementsSection Component for Profile.js
 const EndorsementsSection = ({ profileData, handleBrandClick, showCustomAlert, isOwnProfile }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [displayedCurrentBrands, setDisplayedCurrentBrands] = useState([]);
  const [displayedPastBrands, setDisplayedPastBrands] = useState([]);
  const [pendingCurrentBrands, setPendingCurrentBrands] = useState([]);
  const [pendingPastBrands, setPendingPastBrands] = useState([]);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Track initialization state
  
  // Initialize data from profileData
  useEffect(() => {
    if (profileData?.userId) {
      // Set displayed brands from the arrays in profileData - filter out any with isPending flag
      const currentBrands = profileData.endorsedBrands || [];
      const pastBrands = profileData.pastEndorsedBrands || [];
      
      // For non-owner, only show confirmed brands
      if (!isOwnProfile) {
        setDisplayedCurrentBrands(currentBrands.filter(brand => !brand.isPending));
        setDisplayedPastBrands(pastBrands.filter(brand => !brand.isPending));
      } else {
        // For owner, show all brands and separate pending from confirmed
        setDisplayedCurrentBrands(currentBrands.filter(brand => !brand.isPending));
        setDisplayedPastBrands(pastBrands.filter(brand => !brand.isPending));
        
        // Set pending brands from the main arrays if they have isPending flag
        setPendingCurrentBrands(currentBrands.filter(brand => brand.isPending));
        setPendingPastBrands(pastBrands.filter(brand => brand.isPending));
        
        // Also fetch any pending requests from subcollection
        fetchPendingEndorsements();
      }
      
      // Use counter fields to determine if there are more items
      setHasMoreCurrent((profileData.endorsedBrandsCount || 0) > 3);
      setHasMorePast((profileData.pastEndorsedBrandsCount || 0) > 3);
      
      // Mark initialization as complete
      setIsInitialized(true);
    }
  }, [profileData, isOwnProfile]);
  
  // Fetch pending endorsements from subcollection
  const fetchPendingEndorsements = async () => {
    if (!profileData?.userId) return;
    
    try {
      setIsLoading(true);
      const db = getFirestoreInstance();
      
      const pendingRef = collection(db, 'users', profileData.userId, 'pendingEndorsements');
      const pendingSnapshot = await getDocs(pendingRef);
      
      if (!pendingSnapshot.empty) {
        // Create arrays for current and past pending endorsements
        const currentPending = [];
        const pastPending = [];
        
        pendingSnapshot.docs.forEach(doc => {
          const endorsementData = {
            ...doc.data(),
            docId: doc.id,
            docRef: doc.ref,
            isPending: true
          };
          
          // Sort into current or past based on isPast flag
          if (endorsementData.isPast) {
            pastPending.push(endorsementData);
          } else {
            currentPending.push(endorsementData);
          }
        });
        
        // Update state with pending endorsements - avoid duplicates by checking endorsementId
        setPendingCurrentBrands(prev => {
          const existingIds = new Set(prev.map(brand => brand.endorsementId));
          const newPendingBrands = currentPending.filter(req => !existingIds.has(req.endorsementId));
          return [...prev, ...newPendingBrands];
        });
        
        setPendingPastBrands(prev => {
          const existingIds = new Set(prev.map(brand => brand.endorsementId));
          const newPendingBrands = pastPending.filter(req => !existingIds.has(req.endorsementId));
          return [...prev, ...newPendingBrands];
        });
      }
    } catch (error) {
      console.error('Error fetching pending endorsements:', error);
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Error',
          message: 'Failed to load pending endorsement requests.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more brands from subcollection
  const loadMoreBrands = async (isPast = false) => {
    if (isLoading || !profileData?.userId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const endorsementsRef = collection(db, 'users', profileData.userId, 'endorsements');
      
      // Get currently displayed brands based on tab
      const displayedBrands = isPast ? displayedPastBrands : displayedCurrentBrands;
      
      // Skip already displayed brands
      const displayedBrandNames = displayedBrands.map(brand => brand.brandName.toLowerCase());
      
      // Query the next batch
      const brandsQuery = query(
        endorsementsRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(displayedBrands.length + 3) // Get all current plus 3 more
      );
      
      const brandsSnapshot = await getDocs(brandsQuery);
      
      if (!brandsSnapshot.empty) {
        const allBrands = brandsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out brands that are already displayed
        const newBrands = allBrands.filter(brand => 
          !displayedBrandNames.includes(brand.brandName.toLowerCase())
        );
        
        // Update displayed brands
        if (newBrands.length > 0) {
          if (isPast) {
            const updatedBrands = [...displayedPastBrands, ...newBrands];
            setDisplayedPastBrands(updatedBrands);
            
            // Update hasMore flag based on counter - only if there are more than what we have
            setHasMorePast((profileData.pastEndorsedBrandsCount || 0) > updatedBrands.length);
          } else {
            const updatedBrands = [...displayedCurrentBrands, ...newBrands];
            setDisplayedCurrentBrands(updatedBrands);
            
            // Update hasMore flag based on counter - only if there are more than what we have
            setHasMoreCurrent((profileData.endorsedBrandsCount || 0) > updatedBrands.length);
          }
        } else {
          if (isPast) {
            setHasMorePast(false);
          } else {
            setHasMoreCurrent(false);
          }
        }
      } else {
        if (isPast) {
          setHasMorePast(false);
        } else {
          setHasMoreCurrent(false);
        }
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} brands:`, error);
      if (showCustomAlert) {
        showCustomAlert({
          title: 'Error',
          message: `Failed to load more ${isPast ? 'past' : 'current'} endorsements.`,
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle canceling a pending endorsement request
  const handleCancelPendingEndorsement = async (brand) => {
    if (!brand.docRef && !brand.docId) return;
    
    try {
      setIsLoading(true);
      
      if (brand.docRef) {
        await deleteDoc(brand.docRef);
      } else {
        const db = getFirestoreInstance();
        await deleteDoc(doc(db, 'users', profileData.userId, 'pendingEndorsements', brand.docId));
      }
      
      // Remove from UI
      if (brand.isPast) {
        setPendingPastBrands(prev => 
          prev.filter(item => item.endorsementId !== brand.endorsementId)
        );
      } else {
        setPendingCurrentBrands(prev => 
          prev.filter(item => item.endorsementId !== brand.endorsementId)
        );
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Pending endorsement request canceled',
        type: 'success'
      });
    } catch (error) {
      console.error('Error canceling endorsement request:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to cancel the request',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format "since" date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    // Calculate years and months since
    const now = new Date();
    const startDate = new Date(parseInt(year), parseInt(month) - 1);
    
    let yearsSince = now.getFullYear() - startDate.getFullYear();
    let monthsSince = now.getMonth() - startDate.getMonth();
    
    if (monthsSince < 0) {
      yearsSince--;
      monthsSince += 12;
    }
    
    let sinceText = '';
    if (yearsSince > 0) {
      sinceText += `${yearsSince} year${yearsSince !== 1 ? 's' : ''}`;
      if (monthsSince > 0) {
        sinceText += `, ${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
      }
    } else if (monthsSince > 0) {
      sinceText = `${monthsSince} month${monthsSince !== 1 ? 's' : ''}`;
    } else {
      sinceText = 'This month';
    }
    
    return `Since ${monthName} ${year} (${sinceText})`;
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };

  // Render a brand card
  const renderBrandCard = (brand, isPending = false) => {
    return (
      <div 
        key={brand.endorsementId || brand.brandName} 
        className={`${styles.actCard} ${isPending ? styles.pendingCard : ''}`}
        onClick={isPending ? null : () => handleBrandClick(brand)}
      >
        <img 
          src={brand.logo || defaultProfilePhoto}
          alt={brand.brandName}
          className={styles.actPhoto}
          onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
        />
        <div className={styles.actInfo}>
          <div className={styles.actNameContainer}>
            <h4>{brand.brandName}</h4>
          </div>
          
          {/* Display since date if available */}
          {!brand.isPast && brand.since && (
            <div className={styles.sinceBadge}>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{formatSince(brand.since)}</span>
            </div>
          )}
          
          {/* Display duration if available */}
          {brand.isPast && brand.duration && (
            <div className={styles.durationBadge}>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{formatDuration(brand.duration)}</span>
            </div>
          )}
          
          {/* Display pending badge */}
          {isPending && (
            <div className={styles.pendingBadge}>
              <FontAwesomeIcon icon={faClock} /> Pending Approval
            </div>
          )}
        </div>
        
        {isPending && (
          <button 
            className={styles.cancelButton}
            onClick={(e) => {
              e.stopPropagation();
              handleCancelPendingEndorsement(brand);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    );
  };

  // If still initializing, don't render anything yet to prevent flicker
  if (!isInitialized) {
    return null; // Or return a subtle loading placeholder if preferred
  }

  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Endorsements</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {/* Current Endorsements */}
      {activeTab === 'current' && (
        <>
          {(displayedCurrentBrands.length > 0 || (isOwnProfile && pendingCurrentBrands.length > 0)) ? (
            <>
              <div className={styles.associatedActsGrid}>
                {/* Render pending endorsements first (only for own profile) */}
                {isOwnProfile && pendingCurrentBrands.map(brand => 
                  renderBrandCard(brand, true)
                )}
                
                {/* Render approved endorsements */}
                {displayedCurrentBrands.map(brand => 
                  renderBrandCard(brand)
                )}
              
                {isOwnProfile && (
                  <button 
                    className={styles.addMoreActsButton}
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add More
                  </button>
                )}
              </div>

              {/* Load More button for current endorsements - only show if there are more than we're displaying */}
              {hasMoreCurrent && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreBrands(false)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No current endorsements yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => navigate('/edit-profile')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Endorsements
                </button>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Past Endorsements */}
      {activeTab === 'past' && (
        <>
          {(displayedPastBrands.length > 0 || (isOwnProfile && pendingPastBrands.length > 0)) ? (
            <>
              <div className={styles.associatedActsGrid}>
                {/* Render pending past endorsements first (only for own profile) */}
                {isOwnProfile && pendingPastBrands.map(brand => 
                  renderBrandCard(brand, true)
                )}
                
                {/* Render approved past endorsements */}
                {displayedPastBrands.map(brand => 
                  renderBrandCard(brand)
                )}
              
                {isOwnProfile && (
                  <button 
                    className={styles.addMoreActsButton}
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add More
                  </button>
                )}
              </div>

              {/* Load More button for past endorsements - only show if there are more than we're displaying */}
              {hasMorePast && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreBrands(true)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No past endorsements yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => navigate('/edit-profile')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Past Endorsements
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Set default active tab based on user type


const setDefaultActiveTab = (userType) => {
  switch (userType) {
    case 'Vendor':
      setActiveTab('inventory');
      break;
    case 'Organization':
      setActiveTab('reviews');
      break;
    case 'Crew':
    case 'Staff':
      // Always set dates as the active tab for Crew and Staff
      setActiveTab('dates');
      break;
    default:
      setActiveTab('dates');
      break;
  }
};

// Helper function to determine what tabs to show based on user type
const getTabsForUserType = (userType) => {
  switch (userType) {
    case 'Artist':
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt },
      ];
    case 'Crew':
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt },
        { id: 'feedback', label: 'Feedback', icon: faComment }
      ];
    case 'Manager':
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt }
      ];
    case 'Vendor':
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt },
        { id: 'inventory', label: 'Inventory', icon: faStore },
        { id: 'reviews', label: 'Reviews', icon: faStar }
      ];
    case 'Staff':
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt },
        { id: 'feedback', label: 'Feedback', icon: faComment }
      ];
    case 'Organization':
      return [
        { id: 'reviews', label: 'Reviews', icon: faStar },
        // Note: Organization doesn't have dates tab
      ];
    default:
      return [
        { id: 'dates', label: 'Event Dates', icon: faCalendarAlt }
      ];
  }
};

// Helper function for rendering sections based on user type
const renderUserTypeSections = () => {
  if (!profileData) return null;
  
  switch (profileData.userType) {
    case 'Artist':
      return (
        <>
          <AssociatedActsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigateToUserProfile={(userName) => navigate(userName)}
            showCustomAlert={showCustomAlert}
          />
          <EndorsementsSection
            profileData={profileData}
            handleBrandClick={handleBrandClick}
            showCustomAlert={showCustomAlert}
            isOwnProfile={isOwnProfile}
          />
          <EPKSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            handleEPKView={handleEPKView}
            handleEPKUpload={handleEPKUpload}
            handleEPKDelete={handleEPKDelete}
            openEPKBuilder={handleOpenEPKBuilder}
            customEPK={customEPK}
            updateEPKPreference={updateEPKPreference}
          />
          <ManagedBySection profileData={profileData}
              isOwnProfile={isOwnProfile}
              navigate={navigate}
               />
        </>
      );
    case 'Crew':
      return (
        <>
          <AssociatedActsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigateToUserProfile={(userName) => navigate(userName)}
            showCustomAlert={showCustomAlert}
          />
          <EndorsementsSection
            profileData={profileData}
            handleBrandClick={handleBrandClick}
            showCustomAlert={showCustomAlert}
            isOwnProfile={isOwnProfile}
          />
        </>
      );
    case 'Manager':
      return (
        <>
          <AssociatedActsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigateToUserProfile={(userName) => navigate(userName)}
            showCustomAlert={showCustomAlert}
          />
          <OrganizationsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigate={navigate}
          />
        </>
      );
    case 'Vendor':
      return (
        <>
          <StaffSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigate={navigate}
          />
        </>
      );
    case 'Staff':
      return (
        <>
      
          <AssociatedVendorsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigate={navigate}
          />
        </>
      );
    case 'Organization':
      return (
        <>
          <ManagingActsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigate={navigate}
          />
          <ManagersSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            navigate={navigate}
          />
          <ExecutivesSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            showCustomAlert={showCustomAlert}
          />
          <ContactsSection
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            showCustomAlert={showCustomAlert}
          />
        </>
      );
    default:
      return null;
  }
};

// Helper function for tab content based on user type and active tab
const renderTabContent = () => {
  switch (activeTab) {
    case 'dates':
      return (
        <EventDates 
          userData={profileData}
          events={events}
        />
      );
    case 'inventory':
      return (
        <VendorInventoryTab 
          profileData={profileData}
          isMobile={isMobile}
        />
      );
    case 'feedback':
      return (
        <FeedbackSection 
          profileData={profileData} 
          isOwnProfile={isOwnProfile} 
          showCustomAlert={showCustomAlert} 
        />
      );
    case 'reviews':
      return (
        <RatingsReviewsSection 
          profileData={profileData} 
          isOwnProfile={isOwnProfile} 
          showCustomAlert={showCustomAlert} 
        />
      );
    default:
      return <div>Tab content not available.</div>;
  }
};


const updateEPKPreference = async (epkType) => {
  try {
    const db = getFirestoreInstance();
    const userRef = doc(db, 'users', currentUserData.userId);
    
    // Update the preferredEPKType field in Firestore
    await updateDoc(userRef, {
      preferredEPKType: epkType
    });
    
    // Update local state
    setProfileData(prev => ({
      ...prev,
      preferredEPKType: epkType
    }));
    
    return true;
  } catch (error) {
    console.error('Error updating EPK preference:', error);
    throw error;
  }
};


  // First, let's add a new PitchDeckSection component to render in the profile

  const handleEPKView = (epk) => {
    if (!epk || !epk.fileUrl) return;
    
    setSelectedAttachment({
      fileUrl: epk.fileUrl,
      fileName: epk.fileName || 'Electronic Press Kit',
      fileType: 'application/pdf'
    });
    setShowEPKModal(true); // Reuse existing FileViewer modal
  };
  
  const handleEPKUpload = async (file, uploadResult) => {
    if (!file || !currentUserData?.userId) return;
    
    try {
      setIsEpkUploading(true);
      
      // If uploadResult is not provided, upload the file
      let result = uploadResult;
      if (!result) {
        // Create form data for the upload
        const formData = new FormData();
        formData.append('media', file);
        formData.append('type', 'attachment');
        formData.append('context', 'epk');
        
        // Upload the file with context=epk
        const uploadResponse = await fetch(`/api/processAndUploadMedia?context=epk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          },
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload EPK');
        }
        
        result = await uploadResponse.json();
      }
      
      console.log('EPK upload result:', result);
      
      // Check if the upload was successful and has required fields
      if (!result || !result.url) {
        throw new Error('Missing file URL in upload response');
      }
      
      // If successful, update the user's profile with the EPK info
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', currentUserData.userId);
      
      // If there's an existing EPK, prepare to delete it later
      const oldEPK = profileData?.epk;
      
      // Create the EPK object with the correct field names
      const epkObject = {
        fileUrl: result.url,                // The key field
        filePath: result.filePath,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      };
      
      // Update the user document with the new EPK info
      await updateDoc(userRef, {
        epk: epkObject,
        // Also set these at the root level for easier access if needed
        epkFileUrl: result.url,
        epkFilePath: result.filePath
      });
      
      // Delete the old file if it exists
      if (oldEPK?.filePath) {
        try {
          await fetch('/api/deleteBackblazeFile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ filePath: oldEPK.filePath })
          });
        } catch (deleteError) {
          console.warn('Failed to delete old EPK file:', deleteError);
          // Continue anyway - this is not critical
        }
      }
      
      // Update the local state to reflect the changes
      setProfileData(prev => ({
        ...prev,
        epk: epkObject,
        epkFileUrl: result.url,
        epkFilePath: result.filePath
      }));
      
      showCustomAlert({
        title: "Success",
        message: "EPK uploaded successfully!",
        type: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error uploading EPK:', error);
      showCustomAlert({
        title: "Error",
        message: `Failed to upload EPK: ${error.message}`,
        type: "error"
      });
      return false;
    } finally {
      setIsEpkUploading(false);
    }
  };
  
  const handleEPKDelete = async (epk) => {
    if (!epk?.filePath || !currentUserData?.userId) return;
    
    try {
      setIsEpkUploading(true);
      
      // Delete the file from storage
      const deleteResponse = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath: epk.filePath })
      });
      
      if (!deleteResponse.ok) {
        throw new Error('Failed to delete EPK file');
      }
      
      // Remove the EPK info from the user's profile
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', currentUserData.userId);
      
      await updateDoc(userRef, {
        epk: deleteField()  // Using Firestore's deleteField() to remove the field
      });
      
      // Update the local state to reflect the changes
      setProfileData(prev => {
        const { epk, ...rest } = prev;
        return rest;
      });
      
      showCustomAlert({
        title: "Success",
        message: "EPK removed successfully!",
        type: "success"
      });
    } catch (error) {
      console.error('Error deleting EPK:', error);
      showCustomAlert({
        title: "Error",
        message: "Failed to remove EPK. Please try again.",
        type: "error"
      });
    } finally {
      setIsEpkUploading(false);
    }
  };
  
  // Function to handle opening the EPK builder
  const handleOpenEPKBuilder = (mode = 'create') => {
    setEPKBuilderMode(mode);
    setShowEPKBuilder(true);
  };
  
  // Function to fetch custom EPK data
  const fetchCustomEPK = async (userId) => {
    try {
      console.log('Fetching custom EPK for user:', userId);
      const db = getFirestoreInstance();
      const epkRef = doc(db, 'users', userId, 'epk', 'custom');
      const epkDoc = await getDoc(epkRef);
      
      if (epkDoc.exists()) {
        const epkData = epkDoc.data();
        console.log('Found custom EPK:', epkData);
        setCustomEPK(epkData);
      } else {
        console.log('No custom EPK found for user');
        setCustomEPK(null);
      }
    } catch (error) {
      console.error('Error fetching custom EPK:', error);
      setCustomEPK(null);
    }
  };

  
  // Add this to useEffect that fetches profile data
useEffect(() => {
  // After profile data is fetched, also fetch custom EPK
  if (profileData?.userId) {
    console.log('Profile data loaded, fetching custom EPK for:', profileData.userId);
    fetchCustomEPK(profileData.userId);
  }
}, [profileData?.userId]);
  
  // Function to save custom EPK
  const handleSaveEPK = async (epkData, isPublishing) => {
    try {
      // Log the data being saved for debugging
      console.log('Saving EPK data:', epkData, 'isPublishing:', isPublishing);
      
      // Validate the data
      if (!epkData || !currentUserData?.userId) {
        console.error('Missing required data for saving EPK', { epkData, userId: currentUserData?.userId });
        
        showCustomAlert({
          title: "Error",
          message: "Missing required information. Please try again.",
          type: "error"
        });
        
        return false;
      }
      
      const db = getFirestoreInstance();
      const epkRef = doc(db, 'users', currentUserData.userId, 'epk', 'custom');
      
      // Make sure we're not modifying the original data
      const dataToSave = {
        ...epkData,
        updatedAt: new Date().toISOString(),
        isActive: isPublishing ? true : (epkData.isActive || false)
      };
      
      // Log the actual data being written to Firestore
      console.log('Writing to Firestore:', dataToSave);
      
      // Update the document with the new EPK data
      await setDoc(epkRef, dataToSave);
      
      // Update local state
      setCustomEPK(dataToSave);
      
      // Close the EPK builder if publishing
      if (isPublishing) {
        setShowEPKBuilder(false);
      }
      
      // Show success message
      showCustomAlert({
        title: "Success",
        message: isPublishing 
          ? "Your EPK has been published and is now live!" 
          : "Your EPK has been saved!",
        type: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error saving EPK:', error);
      
      // Show a more detailed error message
      showCustomAlert({
        title: "Error",
        message: `Failed to save EPK: ${error.message || 'Unknown error'}. Please try again.`,
        type: "error"
      });
      
      return false;
    }
  };
  
  
  // Handle messaging
  const isUserOnEvent = (event, userId) => {
    if (!event) return false;
    
    // Check if the user is part of any personnel on this event
    return event.assignedUsers?.some(user => user.userId === userId) || false;
  };

  const handleMessageUser = () => {
    if (!profileData?.userId) return;
    
    // Check 1: Membership tier - most efficient check
    const hasSufficientTier = 
      currentUserData?.membershipTier === 'Pro' || 
      currentUserData?.membershipTier === 'Elite';
    
    if (hasSufficientTier) {
      proceedWithMessaging();
      return;
    }
    
    // Check 2: Connection status - already in state
    if (isConnected) {
      proceedWithMessaging();
      return;
    }
    
    // Check 3: Same event in current month
    if (contextSelectedEvent && isEventInCurrentMonth(contextSelectedEvent)) {
      if (isUserOnEvent(contextSelectedEvent, profileData.userId)) {
        proceedWithMessaging();
        return;
      }
    }
    
    setShowMessageRestrictionModal(true);
  };

  const proceedWithMessaging = () => {
    // Create a simplified user object with all necessary fields
    const directContactUser = {
      userId: profileData.userId,
      fullName: profileData.fullName || 'User',
      userName: profileData.userName || '',
      profilePictureUrl: profileData.profilePictureUrl || '',
      userType: profileData.userType || 'User',
      directContact: true,
      lastMessage: '',
      timestamp: Date.now()
    };
    
    // Use sessionStorage to survive navigation
    sessionStorage.setItem('directContactUser', JSON.stringify(directContactUser));
    
    // Navigate to messages screen
    console.log('[PROFILE] Navigating to messages with direct contact:', directContactUser.fullName);
    navigate('/messages');
  };

  const handleEmailClick = () => {
    if (isOwnProfile) {
      window.location.href = `mailto:${profileData.email}`;
      return;
    }
    
    switch (profileData?.emailPrivacy) {
      case 'public':
        window.location.href = `mailto:${profileData.email}`;
        break;
      case 'connections':
        if (isConnected) {
          window.location.href = `mailto:${profileData.email}`;
        } else {
          setEmailPermissionMessage('This user only shares their email with connections.');
          setShowEmailPermissionModal(true);
        }
        break;
      case 'private':
      default:
        setEmailPermissionMessage('This user has set their email to private.');
        setShowEmailPermissionModal(true);
        break;
    }
  };

  // Handle post operations
  const handleOpenPost = (post) => {
    if (!profileData?.userId) return;
    
    // Always try to get posts from cache first
    const cachedPosts = getCachedPosts(profileData.userId);
    
    // If we have cached posts, use them
    if (cachedPosts && cachedPosts.length > 0) {
      console.log('Using cached posts for modal');
      const postIndex = cachedPosts.findIndex(p => p.postId === post.postId);
      
      if (postIndex !== -1) {
        const returnPath = location.pathname === `/${username}` ? null : `/${username}`;
        openPostModal(cachedPosts, postIndex, returnPath);
        return;
      }
    }
    
    // Fallback to current posts if cache miss
    const postIndex = posts.findIndex(p => p.postId === post.postId);
    if (postIndex !== -1) {
      const returnPath = location.pathname === `/${username}` ? null : `/${username}`;
      openPostModal(posts, postIndex, returnPath);
      
      // Update cache with current posts if not already cached
      if (!cachedPosts || cachedPosts.length === 0) {
        console.log('Caching posts for future use');
        cachePosts(profileData.userId, posts);
      }
    }
  };

  const handleEditProfile = () => navigate('/edit-profile');

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
    setShowBrandsModal(true);
  };

  const handleSharePost = (post) => {
    const shareUrl = `${window.location.origin}/p/${post.postId}`;
    
    setSharePostData({
      ...post,
      type: 'post'
    });
    setShowShareModal(true);

    const shareData = {
      title: `${post.userName}'s Post on OnTour`,
      text: post.caption ? post.caption.substring(0, 100) : 'Check out this post on OnTour',
      url: shareUrl
    };
    
    if (navigator.share) {
      navigator.share(shareData)
        .catch(err => {
          console.error('Error sharing:', err);
          handleClipboardCopy(shareUrl);
        });
    } else {
      handleClipboardCopy(shareUrl);
    }
  };

  const handleClipboardCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Profile URL copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleTagSelect = (tag) => {
    const normalizedTag = tag === 'all' ? 'all' : tag.toLowerCase().replace(/\s+/g, '');
    setSelectedTag(normalizedTag);
    setDisplayedPosts(4);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsModalOpen(true);
  };
  
  const handleDeletePost = async (postId) => {
    try {
      if (window.confirm('Are you sure you want to delete this post?')) {
        const post = posts.find(p => p.postId === postId);
        
        if (post && post.isUploaded) {
          await deleteMediaPost(currentUserData.userId, postId);
        } else {
          await deletePost(currentUserData.userId, postId);
        }
        
        removePost(currentUserData.userId, postId);
        
        const updatedPosts = posts.filter(p => p.postId !== postId);
        setPosts(updatedPosts);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleReportPost = (post) => {
    setPostToReport(post);
    setShowReportModal(true);
  };

  const handleUpload = async (postData) => {
    try {
      if (!postData.mediaFiles && postData.type === 'audio' && postData.embedData) {
        if (!validatePostUrl(postData.embedData.url, 'audio')) {
          throw new Error('Invalid URL for selected platform');
        }
      }
  
      let newPost;
  
      if (editingPost) {
        newPost = await updatePost(profileData.userId, editingPost.postId, {
          ...postData,
          userName: profileData.userName
        });

        if (profileData?.userId) {
          clearUserPostsCache(profileData.userId);
        }
  
        setPosts(prevPosts => 
          prevPosts.map(p => 
            p.postId === editingPost.postId 
              ? { ...p, ...newPost }
              : p
          )
        );
      } else {
        const now = new Date();
        
        newPost = await createPostWithMedia(profileData.userId, {
          ...postData,
          userName: profileData.userName,
        });

        if (profileData?.userId) {
          clearUserPostsCache(profileData.userId);
        }
      
        setPosts(prevPosts => [{
          ...newPost,
          createdAt: {
            seconds: Math.floor(now.getTime() / 1000),
            nanoseconds: 0
          }
        }, ...prevPosts].slice(0, displayedPosts));
      }
  
      setEditingPost(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error handling post:', error);
      
      if (error.message.includes('Invalid URL')) {
        alert('Please provide a valid platform URL for audio posts.');
      } else {
        alert('Failed to upload post. Please try again.');
      }
      
      throw error;
    }
  };

  const loadMorePosts = useCallback(async () => {
    if (loadingMoreRef.current || !hasMorePosts || !profileData?.userId) return;
    
    loadingMoreRef.current = true;
    try {
      setIsPostsLoading(true);
      const newCount = displayedPosts + 9;
      setDisplayedPosts(newCount);
      
      // Check if we can use cached posts
      const cachedPosts = getCachedPosts(profileData.userId);
      if (cachedPosts && selectedTag === 'all' && newCount <= cachedPosts.length) {
        setPosts(cachedPosts.slice(0, newCount));
        setHasMorePosts(cachedPosts.length > newCount);
      } else {
        const postsData = await getUserPosts(
          profileData.userId, 
          newCount,
          selectedTag === 'all' ? null : selectedTag
        );
        
        setPosts(postsData);
        
        // Update cache if showing all tags
        if (selectedTag === 'all') {
          cachePosts(profileData.userId, postsData);
        }
        
        setHasMorePosts(postsData.length >= newCount);
      }
      
      // If we're looking for a specific post after loading more
      if (locationState.showPostModal && locationState.postId) {
        const postIndex = posts.findIndex(p => p.postId === locationState.postId);
        if (postIndex !== -1) {
          openPostModal(posts, postIndex, `/${username}`);
        }
      }
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setIsPostsLoading(false);
      loadingMoreRef.current = false;
    }
  }, [displayedPosts, hasMorePosts, profileData?.userId, selectedTag, locationState, username, openPostModal, getCachedPosts, cachePosts, posts]);

  const handleModalClose = () => {
    setEditingPost(null);
    setIsModalOpen(false);
  };

  const renderContactButton = () => {
    return (
      <>
        <button 
          onClick={() => setShowContactOptions(!showContactOptions)} 
          className={styles['action-button']}
        >
          <FontAwesomeIcon icon={faEnvelope} />
          Contact
        </button>
        
        {showContactOptions && (
          <div className={styles['contact-options']}>
            <button 
              onClick={handleEmailClick} 
              className={styles['action-button']}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Email
            </button>
            <button 
              onClick={handleMessageUser} 
              className={styles['contact-option']}
            >
              <FontAwesomeIcon icon={faComment} />
              Message
            </button>
          </div>
        )}
      </>
    );
  };

  const RecommendationCooldownIndicator = ({ profileId }) => {
    const [cooldownStatus, setCooldownStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
      const checkCooldown = async () => {
        if (!profileId || !currentUserData?.userId) return;
        
        try {
          setLoading(true);
          const status = await checkRecommendationCooldown(
            currentUserData.userId,
            profileId
          );
          setCooldownStatus(status);
        } catch (error) {
          console.error("Error checking cooldown:", error);
        } finally {
          setLoading(false);
        }
      };
      
      checkCooldown();
    }, [profileId, currentUserData?.userId]);
    
    if (loading) return null;
    
    if (!cooldownStatus || !cooldownStatus.inCooldown) return null;
    
    return (
      <div className={styles.cooldownIndicator}>
        <FontAwesomeIcon icon={faClock} />
        <span>
          You can request another recommendation in {formatCooldownTimeRemaining(cooldownStatus.cooldownEndDate)}
        </span>
      </div>
    );
  };

  // Fetch tags
  useEffect(() => {
    const fetchTags = async () => {
      if (profileData?.userId) {
        const tags = await getUserTags(profileData.userId);
        setUserTags(tags);
      }
    };
    fetchTags();
  }, [profileData?.userId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch posts effect
  useEffect(() => {
    const fetchPosts = async () => {
      if (!profileData?.userId) return;
      
      // Check cache first
      const cachedPosts = getCachedPosts(profileData.userId);
      
      // If we have cached posts and not changing filters, use them
      if (cachedPosts && selectedTag === 'all' && displayedPosts <= cachedPosts.length) {
        setPosts(cachedPosts.slice(0, displayedPosts));
        setHasMorePosts(cachedPosts.length > displayedPosts);
        setIsPostsLoading(false);
        setIsPageLoading(false);
        return;
      }
      
      try {
        setIsPostsLoading(true);
        
        const postsData = await getUserPosts(
          profileData.userId, 
          displayedPosts,
          selectedTag === 'all' ? null : selectedTag
        );
        
        setPosts(postsData);
        
        // Cache posts if showing all tags
        if (selectedTag === 'all') {
          cachePosts(profileData.userId, postsData);
        }
        
        // Check if there are more posts
        setHasMorePosts(postsData.length >= displayedPosts);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setIsPostsLoading(false);
        setIsPageLoading(false);
      }
    };

    fetchPosts();
  }, [profileData?.userId, displayedPosts, selectedTag, getCachedPosts, cachePosts]);

  // Handle click outside for contact options
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContactOptions) {
        setShowContactOptions(false);
      }
    };
  
    // Add event listener to document body
    document.body.addEventListener('click', handleClickOutside);
    
    // Clean up
    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, [showContactOptions]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles['loading-container']}>
        <div className={styles['loading-spinner']}></div>
      </div>
    );
  }

  // Render not found state
  if (!profileData) {
    return (
      <div className={styles['profile-container']}>
        <div className={styles['main-layout']}>
          {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
          {!isMobile && (
            <RightMenu
              selectedEvent={selectedEvent}
              events={events}
              onEventSelect={handleEventSelect}
            />
          )}
          <div className={styles['not-found-container']}>
            <div className={styles['not-found-content']}>
              <div className={styles['not-found-artwork']}>
                <FontAwesomeIcon 
                  icon={faUser} 
                  className={styles['not-found-icon']} 
                />
                <div className={styles['not-found-circle']}></div>
              </div>
              <h1>User Not Found</h1>
              <p>Sorry, this profile doesn't exist or may have been removed.</p>
              {currentUserData && (
                <button 
                  className={styles['return-button']}
                  onClick={() => navigate(`/${currentUserData.userName}`)}
                >
                  <FontAwesomeIcon icon={faUser} />
                  Go to Your Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles['profile-container']}>
        <div className={styles['main-layout']}>
          {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
          {!isMobile && (
            <RightMenu
              selectedEvent={selectedEvent}
              events={events}
              onEventSelect={handleEventSelect}
            />
          )}
          
          <div className={styles['profile-main']}>
            <div className={styles['profile-content']}>
              {/* Profile Card */}
              <div className={styles['profile-card']}>
              <div className={styles['cover-photo-container']}>
  <img 
    src={profileData?.profileCoverPictureUrl || defaultCoverPhoto}
    alt="Cover"
    className={styles['cover-photo']}
    onClick={() => {
      setPhotoToView(profileData?.profileCoverPictureUrl || defaultCoverPhoto);
      setShowPhotoModal(true);
    }}
    style={{ cursor: 'pointer' }}
  />
                  <div className={styles['cover-overlay']}></div>
                  
                  {/* Add menu button to cover photo with only Share option */}
                  <button 
                    className={styles['profile-menu-button']}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </button>

                  {!isOwnProfile && (
                    <RecommendationCooldownIndicator profileId={profileData.userId} />
                  )}
                  
                  {isMenuOpen && (
                    <div className={styles['profile-menu']}>
                      <button 
                        className={styles['menu-item']}
                        onClick={() => {
                          handleShareProfile();
                          setIsMenuOpen(false);
                        }}
                      >
                        <FontAwesomeIcon icon={faShare} />
                        Share Profile
                      </button>
                      
                      {!isOwnProfile && (
                        <>
                          <button 
                            className={styles['menu-item']}
                            onClick={() => {
                              handleRecommendationRequest();
                              setIsMenuOpen(false);
                            }}
                            disabled={isRequestingSent}
                          >
                            <FontAwesomeIcon icon={faThumbsUp} />
                            Request Recommendation
                            {isRequestingSent && <FontAwesomeIcon icon={faSpinner} spin className={styles['spin-icon']} />}
                          </button>
                          
                          {/* Add Report Profile option */}
                          <button 
                            className={`${styles['menu-item']} ${styles['menu-item-report']}`}
                            onClick={() => {
                              setShowReportModal(true);
                              setIsMenuOpen(false);
                            }}
                          >
                            <FontAwesomeIcon icon={faFlag} />
                            Report Profile
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Profile header */}
                <div className={styles['profile-header']}>
                  <div className={styles['profile-left']}>
                  <img
  src={profileData?.profilePictureUrl || defaultProfilePhoto}
  alt="Profile"
  className={styles['profile-photo']}
  onClick={() => {
    setPhotoToView(profileData?.profilePictureUrl || defaultProfilePhoto);
    setShowPhotoModal(true);
  }}
  style={{ cursor: 'pointer' }}
/>
                          
                    <div className={styles['profile-info']}>
                      <div className={styles['name-section']}>
                        <h1>{profileData?.fullName}</h1>
                        {profileData?.idVerificationStatus === 'Verified' && (
                          <img src={verifiedBadge} alt="Verified" className={styles['verified-badge']} />
                        )}
                      </div>
    
                      <div className={styles['user-type']}>
  {profileData?.userType}
  {profileData?.userSubType && ` · ${profileData.userSubType}`}
  

</div>
  {/* Display ratings for Vendor and Organization user types */}
  {(profileData?.userType === 'Vendor' || profileData?.userType === 'Organization') && (
    <span 
      className={styles.ratingDisplay} 
      onClick={() => {
        // Set the active tab to reviews
        setActiveTab('reviews');
        
        // Find the content-section element
        setTimeout(() => {
          // Try multiple different selector approaches
          const contentSection = document.querySelector(`.${styles['content-section']}`);
          
          if (contentSection) {
            // Scroll the content section into view with smooth behavior
            contentSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            
            // Add a highlight effect to draw attention to the active tab
            const activeTabElement = document.querySelector(`.${styles.tab}.${styles.active}`);
            if (activeTabElement) {
              // Add temporary highlight class
              activeTabElement.style.transition = 'background-color 0.5s ease';
              activeTabElement.style.backgroundColor = 'rgba(226, 56, 77, 0.1)';
              
              // Remove highlight after animation completes
              setTimeout(() => {
                activeTabElement.style.backgroundColor = '';
              }, 1500);
            }
          }
        }, 100); // Small delay to ensure the tab has been updated in the DOM
      }}
      title="Click to see reviews"
    >
      <StarRating rating={profileData.totalRatingsScore || 0} />
    </span>
  )}
                      <div className={styles['info-items']}>
                        <div className={styles['info-item']}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span>{`${profileData?.city}, ${profileData?.state}, ${profileData?.country}`}</span>
                        </div>

                        <div className={styles['experience-info-item']}>
                        {profileData?.overallExperienceYears !== null && profileData?.overallExperienceYears !== undefined && (
      <div className={styles['info-item']}>
        <FontAwesomeIcon icon={faCalendarAlt} />
        <span>Overall Experience: {profileData.overallExperienceYears} {profileData.overallExperienceYears === 1 ? 'year' : 'years'}</span>
      </div>
    )}
    </div>
    <div className={styles['experience-info-item']}>
    {profileData?.professionalExperienceYears !== null && profileData?.professionalExperienceYears !== undefined && (
      <div className={styles['info-item']}>
        <FontAwesomeIcon icon={faBriefcase} />
        <span>Professional Experience: {profileData.professionalExperienceYears} {profileData.professionalExperienceYears === 1 ? 'year' : 'years'}</span>
      </div>
    )}

    </div>

                        <div className={`${styles['info-item']} ${isUserBooked(events, profileData) ? styles['on-tour'] : styles.available}`}>
                          <FontAwesomeIcon icon={isUserBooked(events, profileData) ? faRoute : faCalendarCheck} />
                          <span>{isUserBooked(events, profileData) ? 'Booked' : 'Available'}</span>
                        </div>
                      </div>

                      {/* Bio Section */}
                      <div className={styles.bioSection}>
                        {profileData?.bio ? (
                          <p className={styles.bio}>{profileData.bio}</p>
                        ) : (
                          isOwnProfile && (
                     
                     
                     
                            <button 
                              onClick={() => navigate('/edit-profile')} 
                              className={styles.addBioButton}
                            >
                              Add bio
                            </button>
                          )
                        )}
                      </div>

                                           {/* Social Media Icons */}
                                           <div className={styles.socialIcons}>
                        {profileData?.socialLinks?.instagramUrl && profileData.socialLinks.instagramUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.instagramUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.socialIconLink}
                            title={profileData.socialLinks.instagramUrl}
                          >
                            <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.twitterUrl && profileData.socialLinks.twitterUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.twitterUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.socialIconLink}
                            title={profileData.socialLinks.twitterUrl}
                          >
                            <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.linkedInUrl && profileData.socialLinks.linkedInUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.linkedInUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.socialIconLink}
                            title={profileData.socialLinks.linkedInUrl}
                          >
                            <FontAwesomeIcon icon={faLinkedin} className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.facebookUrl && profileData.socialLinks.facebookUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.facebookUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.socialIconLink}
                            title={profileData.socialLinks.facebookUrl}
                          >
                            <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.youtubeUrl && profileData.socialLinks.youtubeUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.youtubeUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.socialIconLink}
                            title={profileData.socialLinks.youtubeUrl}
                          >
                            <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.spotifyUrl && profileData.socialLinks.spotifyUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.spotifyUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.customsocialIcon}
                            title={profileData.socialLinks.spotifyUrl}
                          >
                            <img src={spotifylogo}  alt="Spotify" className={styles.socialIcon} />
                          </a>
                        )}
                        {profileData?.socialLinks?.appleMusicUrl && profileData.socialLinks.appleMusicUrl.trim() !== "" && (
                          <a 
                            href={profileData.socialLinks.appleMusicUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.customsocialIcon}
                            title={profileData.socialLinks.appleMusicUrl}
                          >
                            <img src={applemusiclogo}  alt="Apple Music" className={styles.socialIcon} />
                          </a>
                        )}
                        {isOwnProfile && !isAllSocialLinksPresent(profileData?.socialLinks) && (
                          <button 
                            onClick={() => navigate('/edit-profile')} 
                            className={styles.addSocialButton}
                            title="Add social links"
                          >
                            <FontAwesomeIcon icon={faPlus}  />
                          </button>
                          )}
                    
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles['profile-right']}>
                    <StatsContainer 
                      profileData={profileData}
                      events={events}
                      currentUser={currentUserData}
                      onToursClick={handleToursCountClick}
                      onShowsClick={handleShowsCountClick}
                      onRecommendationsClick={handleRecommendationsClick}
                      onConnectionsClick={handleConnectionsClick}
                    />
                  </div>
                </div>

                <div className={styles['profile-actions-container']}>
                  {isOwnProfile ? (
                    <div className={styles['profile-actions']}>
                      {/* Single row with three buttons for own profile */}
                      <div className={styles['action-row']}>
                        <button 
                          onClick={handleEditProfile} 
                          className={styles['action-button']}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          Edit Profile
                        </button>
                        
                        <button 
                          onClick={() => navigate('/my-bands')} 
                          className={styles['action-button']}
                        >
                          <FontAwesomeIcon icon={faMusic} />
                          My Bands
                        </button>
                        
                        <button 
                          onClick={() => navigate('/Settings')} 
                          className={styles['action-button']}
                        >
                          <FontAwesomeIcon icon={faCog} />
                          Settings
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles['profile-actions']}>
                      <div className={styles['action-row']}>
                        {/* First row: Add to Event (if allowed) and Connect/Recommend buttons */}
                        {isAddToEventAllowed() && (
                          <button 
                            className={styles['action-button']}
                            onClick={() => setShowAddOnTourModal(true)}
                          >
                            <img 
                              src={require('../../assets/Images/AddOnTourButton.png')}
                              alt="Add to Event"
                              className={styles['add-tour-icon']}
                            />
                            Add to Event
                          </button>
                        )}
                        
                        <button 
                          className={`${styles['action-button']} ${isConnected ? styles['connected'] : ''} ${connectionRequestSent ? styles['requested'] : ''}`}
                          onClick={toggleConnection}
                          disabled={isConnectingLoading}
                        >
                          <FontAwesomeIcon icon={
                            isConnected ? faCheck : 
                            connectionRequestSent ? faClock : 
                            faUserPlus
                          } />
                          {isConnectingLoading ? 'Processing...' : (
                            isConnected ? 'Connected' : 
                            connectionRequestSent ? 'Requested' : 
                            'Connect'
                          )}
                        </button>
                        
                        <button 
                          className={`${styles['action-button']} ${hasRecommended ? styles['recommended'] : ''}`}
                          onClick={() => hasRecommended ? setShowUnrecommendConfirmModal(true) : toggleRecommendation()}
                          disabled={isRecommendationInProgress}
                        >
                          <FontAwesomeIcon icon={hasRecommended ? faCheck : faThumbsUp} />
                          {isRecommendationInProgress ? 'Processing...' : (hasRecommended ? 'Recommended' : 'Recommend')}
                        </button>
                      </div>
                      
                      <div className={styles['action-row']}>
                        {/* Second row: Contact/Email and Message buttons */}
                        {currentUserData?.userType === 'Manager' ? (
                          renderContactButton()
                        ) : (
                          <>
                            <button 
                              onClick={handleEmailClick} 
                              className={styles['action-button']}
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                              Email
                            </button>
                                  
                            <button 
                              onClick={handleMessageUser} 
                              className={styles['action-button']}
                            >
                              <FontAwesomeIcon icon={faComment} />
                              Message
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            
          

            
            {/* User-type specific sections */}
{renderUserTypeSections()}

              {/* Content Section */}
              <div className={styles['content-section']}>
  {canViewPrivateContent() ? (
    <>
      <div className={styles['content-tabs']}>
        {getTabsForUserType(profileData?.userType).map(tab => (
          <button 
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} className={styles.tabIcon} />
            {tab.label}
          </button>
        ))}
      </div>
      
      <div className={styles['tab-content']}>
        {renderTabContent()}
      </div>
    </>
  ) : (
    <div className={styles['private-account-message']}>
      <FontAwesomeIcon icon={faLock} className={styles['lock-icon']} />
      <h3>This Account is Private</h3>
      <p>Connect with this user to see their content.</p>
    </div>
  )}
</div>
            </div>
          </div>
        </div>

        {/* All modals */}
        {showProfileShareModal && (
          <ShareModal
            isOpen={showProfileShareModal}
            onClose={() => setShowProfileShareModal(false)}
            postData={{
              type: 'profile',
              caption: profileData?.bio || '',
              tags: [],
              postId: profileData?.userId,
            }}
          />
        )}

        {showPhotoModal && (
  <PhotoViewerModal
    isOpen={showPhotoModal}
    onClose={() => setShowPhotoModal(false)}
    photoUrl={photoToView}
    photoType={photoToView === (profileData?.profilePictureUrl || defaultProfilePhoto) ? 'profile' : 'cover'}
  />
)}

        <ToursModal 
          isOpen={toursModalVisible}
          onClose={() => setToursModalVisible(false)}
          events={events}
          userData={profileData}
        />

        <ShowsModal 
          isOpen={showsModalVisible}
          onClose={() => setShowsModalVisible(false)}
          events={events}
          userData={profileData}
        />

        {(profileData?.userType === 'Manager' || profileData?.userType === 'Artist Management Company') && (
          <UsersModal 
            isOpen={usersModalVisible}
            onClose={() => setUsersModalVisible(false)}
            events={events}
            currentUser={currentUserData}
            onUserSelect={handleUserSelect}
          />
        )}
        
        {showBrandsModal && selectedBrand && (
          <BrandsModal 
            brand={selectedBrand} 
            onClose={() => setShowBrandsModal(false)} 
          />
        )}

        {setShowEPKModal && selectedAttachment && (
  <FileViewerModal
    isOpen={showEPKModal}
    onClose={() => setShowEPKModal(false)}
    file={selectedAttachment}
  />
)}

{/* EPK Builder Modal */}
{showEPKBuilder && (
  <EPKBuilder
    isOpen={showEPKBuilder}
    onClose={() => setShowEPKBuilder(false)}
    userData={currentUserData}
    mode={epkBuilderMode}
    existingEPK={customEPK}
    onSave={handleSaveEPK}
    authToken={authToken}
  />
)}

{/* EPK Viewer Modal */}
{customEPK && customEPK.isActive && (
  <EPKViewer
    isOpen={epkBuilderMode === 'view' && showEPKBuilder}
    onClose={() => setShowEPKBuilder(false)}
    epkData={customEPK}
    userName={profileData?.userName}
  />
)}

        <UploadModal 
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onUpload={handleUpload}
          existingTags={userTags}
          editingPost={editingPost}
        />

        <UpgradeModal 
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />

        <ReportModal 
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          profileData={profileData}
          currentUser={currentUserData}
        />

        <ConnectionsModal 
          isOpen={connectionsModalVisible}
          onClose={() => setConnectionsModalVisible(false)}
          profileData={profileData}
        />

        <RecommendationsModal 
          isOpen={recommendationsModalVisible}
          onClose={() => setRecommendationsModalVisible(false)}
          profileData={profileData}
        />

        <TourInvitationModal 
          isOpen={showAddOnTourModal}
          onClose={() => setShowAddOnTourModal(false)}
          userData={currentUserData}
          currentUser={currentUserData}
          profileData={profileData}
        />

        <DisconnectConfirmModal 
          isOpen={showDisconnectConfirmModal}
          onClose={() => setShowDisconnectConfirmModal(false)}
          onConfirm={handleDisconnect}
          isLoading={isConnectingLoading}
        />

        <CancelRequestModal 
          isOpen={showCancelRequestModal}
          onClose={() => setShowCancelRequestModal(false)}
          onConfirm={handleCancelRequest}
          isLoading={isConnectingLoading}
        />

        <UnrecommendConfirmModal 
          isOpen={showUnrecommendConfirmModal}
          onClose={() => setShowUnrecommendConfirmModal(false)}
          onConfirm={handleUnrecommend}
          isLoading={isRecommendationInProgress}
        />

        <MessageRestrictionModal 
          isOpen={showMessageRestrictionModal}
          onClose={() => setShowMessageRestrictionModal(false)}
        />

        <EmailPermissionModal
          isOpen={showEmailPermissionModal}
          onClose={() => setShowEmailPermissionModal(false)}
          message={emailPermissionMessage}
        />

        <CustomModal 
          title={showCustomModal?.title}
          message={showCustomModal?.message}
          onClose={() => setShowCustomModal(null)}
        />

        {/* Alert modal */}
        {showAlert && (
          <AlertModal
            isOpen={showAlert}
            onClose={() => setShowAlert(false)}
            alertConfig={alertConfig}
          />
        )}

        {/* Custom Post Modal */}
        <PostModal
          isOwner={currentUserData?.userId === profileData?.userId}
          onEdit={handleEditPost}
          onDelete={handleDeletePost}
          onReport={handleReportPost}
          onShare={handleSharePost}
          isMobile={isMobileDevice()}
        />
        
        {isPageLoading && (
          <div className={styles['page-loading-overlay']}>
            <div className={styles['loading-spinner']}></div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;

